import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import api from "@/utils/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { FileText, Upload, Save, X, AlertTriangle, Calculator, Loader2, Eye, RotateCw, Boxes, ExternalLink, Sparkles, CheckCircle, Repeat, ChevronUp, ChevronDown, Download, Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";
import AiConsentDialog from "@/components/AiConsentDialog";
import DuplicateWarningDialog from "@/components/DuplicateWarningDialog";
import DuplicateReviewPanel from "@/components/DuplicateReviewPanel";
import AssetActivationWizard from "@/components/anlagen/AssetActivationWizard";
import RecurringExpenseFields from "@/components/expense/RecurringExpenseFields";
import TripLinkSection from "@/components/travel/TripLinkSection";
import FachInfo from "@/components/ui/fach-info";
import { useIsPro } from "@/hooks/useIsPro";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
// Feb 2026 — DACH-Smart-Inputs (Phase 9)
import { CurrencyInput } from "@/components/inputs/SmartInputs";
import { useUserCountry } from "@/hooks/useUserCountry";
import { VAT_RATES_BY_COUNTRY, VAT_RATES_OTHER_DACH, getCountryDefaultRate } from "@/utils/vatRates";
import { getForeignRates } from "@/utils/vatForeign";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feb 2026 — Country-aware VAT-Sätze für Multi-Tax-Editor.
// DACH-spezifisch: jeder User sieht primär die Sätze seines Heimatlandes;
// die anderen DACH-Länder als „weitere"-Gruppe für Cross-Border-Belege.
const getCountryDefaultRate__legacy = (country, position) => {
  // legacy fallback – nicht mehr benutzt (siehe @/utils/vatRates)
  return getCountryDefaultRate(country, position);
};

export default function ExpenseDetail({ expense, isOpen, onClose, onSaved, categories = [], userPlan = 'free', onPrev, onNext, hasPrev = false, hasNext = false }) {
  const { country: userCountry } = useUserCountry();
  // Aug 2026 — „Geprüft“-Status (Toggle im Header, synchron mit der Liste)
  const [reviewed, setReviewed] = useState(false);
  useEffect(() => { setReviewed(!!expense?.is_reviewed); }, [expense?.id, expense?.is_reviewed]);
  const [form, setForm] = useState({});
  const [afaConfig, setAfaConfig] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  // Juni 2026 — separate Blob-URL + Art (image/pdf/file) für die große linke Vorschau (wie SB-Drawer)
  const [receiptBlobUrl, setReceiptBlobUrl] = useState(null);
  const [receiptKind, setReceiptKind] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  // Sep 2026 — merkt, welche KI-Aktion nach der Einwilligung fortgesetzt wird
  const [consentAction, setConsentAction] = useState("upload");
  const [lastUploadedFile, setLastUploadedFile] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState({ open: false, matches: [] });
  const [aiEnabled, setAiEnabled] = useState(false);
  const [receiptZoom, setReceiptZoom] = useState(false);
  const [didSave, setDidSave] = useState(false);
  // Juli 2026 — Beleg-Eingang per E-Mail: optimistischer Bestätigt-Status
  const [inboxConfirmed, setInboxConfirmed] = useState(false);
  useEffect(() => { setInboxConfirmed(false); }, [expense?.id]);
  // Juni 2026 — Vorsteuerpauschalierung aktiv? (steuert Sichtbarkeit des Wareneinkauf/Fremdleistung-Kästchens, nur AT)
  const [vstpActive, setVstpActive] = useState(false);
  useEffect(() => {
    if (!isOpen || (userCountry || "").toUpperCase() !== "AT") { setVstpActive(false); return; }
    let cancelled = false;
    api.get('/vstp/status')
      .then(r => { if (!cancelled) setVstpActive(!!r.data?.vorsteuer_pauschalierung?.enabled); })
      .catch(() => { if (!cancelled) setVstpActive(false); });
    return () => { cancelled = true; };
  }, [isOpen, userCountry]);
  // Feb 2026 — AfA-Modul Anlagen-Aktivierung (gemäß KONZEPT_AFA_2026.md)
  const [activationWizardOpen, setActivationWizardOpen] = useState(false);
  // KI-Vorschlag für Anlagen-Kategorie (aus asset_category_suggestion der KI-Erkennung)
  const [aiAssetCategoryHint, setAiAssetCategoryHint] = useState(null);
  const isPro = useIsPro();
  // Feb 2026 — Recent Teilnehmer + Recent Locations Quick-Pick (Bewirtungs-Card)
  const [recentParticipants, setRecentParticipants] = useState([]);
  const [recentLocations, setRecentLocations] = useState([]);

  // Feb 2026 — Auto-Save (Debounced) bei Feld-Änderung statt Save-Button
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle"); // idle | saving | saved | error
  // Feb 2026 — "Individuell"-Modus für USt-Dropdown (analog AssetCreateWizard)
  const [showCustomVat, setShowCustomVat] = useState(false);
  const initialFormLoadedRef = React.useRef(false);
  const autoSaveTimerRef = React.useRef(null);
  // Juni 2026 — Ref auf die aktuelle handleSave-Funktion (für Enter-zum-Speichern ohne Re-Subscribe).
  const handleSaveRef = React.useRef(null);
  // Juni 2026 — Snapshot der zuletzt gespeicherten Form (JSON) für Dirty-Check.
  // Verhindert Auto-Save-Schleife: ohne echte Änderung wird kein Save ausgelöst.
  const lastSavedFormRef = React.useRef(null);

  // Auto-cleanup: Delete phantom expenses when closing without saving
  const handleClose = async () => {
    if (!didSave && expense?.id && expense?.description === "Neue Ausgabe" && (expense?.amount === 0 || !expense?.amount)) {
      try {
        await api.delete(`/expenses/${expense.id}`);
        onSaved?.(); // Refresh the list
      } catch (e) { /* ignore */ }
    }
    setDidSave(false);
    onClose();
  };

  // Native event listener to close zoom overlay – bypasses Radix event interception
  useEffect(() => {
    if (!receiptZoom) return;
    const handlePointerDown = (e) => {
      const overlay = document.getElementById('receipt-zoom-overlay');
      if (!overlay) return;
      // Don't close if clicking on the image itself
      if (e.target.tagName === 'IMG' && overlay.contains(e.target)) return;
      // Stop Radix from seeing this event (prevents Sheet from closing)
      e.stopImmediatePropagation();
      setReceiptZoom(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        setReceiptZoom(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [receiptZoom]);

  // Juni 2026 — ↑/↓ navigiert zur vorherigen/nächsten Ausgabe der Liste (wie SB-Drawer),
  // außer der Fokus liegt in einem Eingabefeld oder die Vollbild-Vorschau ist offen.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (receiptZoom) return;
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      if (e.key === 'ArrowUp') { e.preventDefault(); onPrev && onPrev(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); onNext && onNext(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, receiptZoom, onPrev, onNext]);

  // Juni 2026 — Enter speichert die Ausgabe (außer in Textarea / Buttons / Dropdowns / Vollbild-Zoom).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return;
      if (receiptZoom || showConsentDialog || duplicateWarning.open) return;
      const el = document.activeElement;
      const tag = el?.tagName;
      if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'SELECT' || tag === 'A' || el?.isContentEditable) return;
      // Offenes Radix-Dropdown (Kategorie/Land etc.) nicht kapern
      if (document.querySelector('[role="listbox"], [role="menu"]')) return;
      e.preventDefault();
      handleSaveRef.current && handleSaveRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, receiptZoom, showConsentDialog, duplicateWarning.open]);



  useEffect(() => {
    if (expense) {
      const initialForm = {
        description: expense.description || "",
        amount: expense.amount || 0,
        date: expense.date ? expense.date.split("T")[0] : "",
        category: expense.category || "other",
        vat_rate: expense.vat_rate ?? 19,
        tax_deductible_type: expense.tax_deductible_type || "full",
        deductible_percentage: expense.deductible_percentage ?? 100,
        private_use_percentage: expense.private_use_percentage ?? 0,
        // Juli 2026 (L2) — Privatanteil-Modus: anteilig (Default) | eigenverbrauch
        private_use_mode: expense.private_use_mode || "anteilig",
        // Juli 2026 (L3) — AT-PKW: Ausnahme vom Vorsteuerabzugs-Verbot
        vehicle_vorsteuer_eligible: !!expense.vehicle_vorsteuer_eligible,
        depreciation_type: expense.depreciation_type || null,
        depreciation_years: expense.depreciation_years || null,
        net_amount: expense.net_amount || (expense.amount ? Math.round((expense.amount / (1 + (expense.vat_rate ?? 19) / 100)) * 100) / 100 : 0),
        vendor: expense.vendor || "",
        invoice_number: expense.invoice_number || "",
        notes: expense.notes || "",
        receipt_file_key: expense.receipt_file_key || null,
        receipt_filename: expense.receipt_filename || null,
        receipt_content_type: expense.receipt_content_type || null,
        storage_uri: expense.storage_uri || null,
        // Feb 2026 — Multi-Tax-Aufschlüsselung
        tax_breakdown: Array.isArray(expense.tax_breakdown) ? expense.tax_breakdown : [],
        multi_tax_suspected: !!expense.multi_tax_suspected,
        // Feb 2026 — Bewirtungsbescheinigung
        hospitality_occasion: expense.hospitality_occasion || "",
        hospitality_participants: expense.hospitality_participants || "",
        hospitality_location: expense.hospitality_location || "",
        // Feb 2026 — Reverse-Charge / Innergem. Erwerb
        is_reverse_charge: !!expense.is_reverse_charge,
        is_intra_community_acquisition: !!expense.is_intra_community_acquisition,
        rc_suspected: !!expense.rc_suspected,
        rc_auto_confirmed: !!expense.rc_auto_confirmed,
        supplier_country: expense.supplier_country || "",
        supplier_vat_id: expense.supplier_vat_id || "",
        // Juni 2026 — Vorsteuerpauschalierung (AT): Wareneinkauf/Fremdleistung
        vstp_sonder_vorsteuer: !!expense.vstp_sonder_vorsteuer,
        // Feb 2026 — FX-Conversion-Audit
        original_amount: expense.original_amount ?? null,
        original_currency: expense.original_currency || null,
        fx_rate: expense.fx_rate ?? null,
        fx_rate_date: expense.fx_rate_date || null,
        fx_source: expense.fx_source || null,
        // Juni 2026 — Wiederkehrende Ausgaben
        is_recurring: !!expense.is_recurring,
        recurrence_interval: expense.recurrence_interval || null,
        recurrence_custom_value: expense.recurrence_custom_value ?? 2,
        recurrence_custom_unit: expense.recurrence_custom_unit || "months",
        recurrence_end_date: expense.recurrence_end_date || "",
      };
      setForm(initialForm);
      // Dirty-Check-Snapshot setzen → Auto-Save feuert erst bei echter Änderung
      lastSavedFormRef.current = JSON.stringify(initialForm);
      // Feb 2026 — Wenn der gespeicherte vat_rate nicht in den Länder-Standardsätzen liegt
      // → "Individuell" vorauswählen (sonst Dropdown würde leer wirken)
      const ctry = (userCountry || "DE").toUpperCase();
      const presetRates = (VAT_RATES_BY_COUNTRY[ctry] || VAT_RATES_BY_COUNTRY.DE).map(r => r.rate);
      const otherRates = (VAT_RATES_OTHER_DACH[ctry] || []).map(r => r.rate);
      const allKnownRates = [...presetRates, ...otherRates];
      const expRate = expense.vat_rate ?? 19;
      setShowCustomVat(!allKnownRates.includes(expRate));
      // Auto-Save (Feb 2026): Initial form load → markiere als unverändert,
      // sodass nicht direkt nach dem Öffnen ein Save ausgelöst wird.
      initialFormLoadedRef.current = false;
      setTimeout(() => { initialFormLoadedRef.current = true; }, 300);
      setAutoSaveStatus("idle");
      // Feb 2026 — Lade Recent-Teilnehmer + Recent-Locations für Quick-Pick
      if (expense.category === "hospitality") {
        api.get("/expenses/hospitality-recent-participants")
          .then((r) => setRecentParticipants(r.data?.participants || []))
          .catch(() => setRecentParticipants([]));
        api.get("/expenses/hospitality-recent-locations")
          .then((r) => setRecentLocations(r.data?.locations || []))
          .catch(() => setRecentLocations([]));
      } else {
        setRecentParticipants([]);
        setRecentLocations([]);
      }
      if (expense.receipt_file_key) {
        // Load receipt via authenticated API call (img src can't send auth headers)
        api.get(`/expenses/${expense.id}/receipt`, { responseType: 'blob' })
          .then(res => {
            const contentType = res.data.type || res.headers?.['content-type'] || '';
            const blobUrl = URL.createObjectURL(res.data);
            setReceiptBlobUrl(blobUrl);
            if (contentType.startsWith('image/')) {
              setReceiptKind('image');
              setReceiptPreviewUrl(blobUrl);
            } else if (contentType.includes('pdf')) {
              setReceiptKind('pdf');
              setReceiptPreviewUrl("pdf");
            } else {
              setReceiptKind('file');
              setReceiptPreviewUrl("file");
            }
          })
          .catch((err) => {
            console.error('Receipt preview load failed:', err?.response?.status, err?.message);
            setReceiptKind('file');
            setReceiptPreviewUrl("file");
            setReceiptBlobUrl(null);
          });
      } else {
        setReceiptPreviewUrl(null);
        setReceiptBlobUrl(null);
        setReceiptKind(null);
      }
    }
  }, [expense]);

  const fetchAfaConfig = useCallback(async () => {
    try {
      const res = await api.get("/expenses/afa-config");
      setAfaConfig(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchAfaConfig();
      // Check AI consent status
      api.get("/user/ai-consent").then(res => {
        setAiEnabled(res.data?.ai_consent === true);
      }).catch(() => setAiEnabled(false));
    }
  }, [isOpen, fetchAfaConfig]);

  // ── Feb 2026 — Auto-Save: Form-Änderung → debounced PATCH (800 ms) ──
  // Spinner-Anzeige ERST wenn der Save wirklich startet (nicht bei jedem Keystroke) — sonst flackert er.
  useEffect(() => {
    if (!isOpen) return;
    if (!expense?.id) return;
    if (!initialFormLoadedRef.current) return;
    if (analyzing || saving || uploading) return; // nicht während AI/Save/Upload triggern
    // Juni 2026 — Dirty-Check: nur speichern, wenn sich die Form wirklich geändert hat.
    // Verhindert Endlos-Schleife (saving toggelt die Effekt-Dependency).
    if (lastSavedFormRef.current !== null && JSON.stringify(form) === lastSavedFormRef.current) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus("saving"); // ← erst hier, damit Spinner nicht bei jedem Keystroke flackert
      try {
        const snapshot = JSON.stringify(form);
        await handleSave(true, true); // skipDuplicateCheck=true, silent=true → Auto-Save lässt Wizard offen, kein Toast
        lastSavedFormRef.current = snapshot; // gespeicherten Stand merken → kein Re-Save
        setAutoSaveStatus("saved");
        setDidSave(true);
        setTimeout(() => setAutoSaveStatus("idle"), 1500);
      } catch (e) {
        console.error("Auto-Save failed:", e);
        setAutoSaveStatus("error");
      }
    }, 800);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-line
  }, [form, isOpen, expense?.id, analyzing, saving, uploading]);

  // Bidirectional net/gross calculation handlers
  const calcNet = (brutto, vatRate) => {
    const b = parseFloat(brutto) || 0;
    const v = parseFloat(vatRate) || 0;
    return v > 0 ? Math.round((b / (1 + v / 100)) * 100) / 100 : b;
  };

  const handleAmountChange = (value) => {
    const netto = calcNet(value, form.vat_rate);
    setForm(f => ({ ...f, amount: value, net_amount: netto }));
  };

  const handleVatChange = (value) => {
    const netto = calcNet(form.amount, value);
    setForm(f => ({ ...f, vat_rate: value, net_amount: netto }));
  };

  const handleNetChange = (value) => {
    const netto = parseFloat(value) || 0;
    const brutto = parseFloat(form.amount) || 0;
    let vatRate = 0;
    if (netto > 0 && brutto > netto) {
      vatRate = Math.round(((brutto / netto) - 1) * 100 * 100) / 100;
    }
    setForm(f => ({ ...f, net_amount: value, vat_rate: vatRate }));
  };

  const netAmount = parseFloat(form.net_amount) || 0;
  const vatAmount = (parseFloat(form.amount) || 0) - netAmount;

  const getAfaSuggestion = () => {
    if (!afaConfig || !netAmount) return null;
    const rules = afaConfig.rules || [];
    for (const rule of rules) {
      if (rule.max_net > 0 && netAmount <= rule.max_net) {
        return { type: rule.type, label: rule.label };
      }
    }
    if (netAmount > (afaConfig.pool_max || 1000)) {
      return { type: "linear", label: "Lineare Abschreibung nach Nutzungsdauer" };
    }
    return null;
  };

  const afaSuggestion = getAfaSuggestion();

  // Auto-apply AfA suggestion when amount changes (only if user hasn't manually set a different type)
  const afaSuggestionType = afaSuggestion?.type;
  useEffect(() => {
    if (afaSuggestionType && !form.depreciation_type) {
      setForm(f => ({ ...f, depreciation_type: afaSuggestionType }));
    }
  }, [afaSuggestionType]);


  const applyExtractedData = (d) => {
    // Feb 2026 — AfA-Modul: KI-Vorschlag für Anlagen-Kategorie speichern (Hint für Wizard)
    if (d.asset_category_suggestion) {
      setAiAssetCategoryHint(d.asset_category_suggestion);
    }
    const newData = {
      description: d.description || d.vendor || "",
      amount: d.amount || d.amount_gross || 0,
      date: d.date ? d.date.split("T")[0] : "",
      vendor: d.vendor || "",
      invoice_number: d.invoice_number || "",
      vat_rate: d.vat_rate ?? 19,
      category: d.category_suggestion || "other",
    };
    const brutto = parseFloat(newData.amount) || 0;
    const vatRate = parseFloat(newData.vat_rate) || 0;
    // Feb 2026 — Backend liefert `net_amount` (nicht `amount_net`).
    // RC/0%-Belege haben net = gross; bei normalen Sätzen rechnen wir aus gross/(1+vat).
    const aiNet = d.net_amount ?? d.amount_net ?? null;
    const netto = (aiNet !== null && aiNet !== undefined && !isNaN(parseFloat(aiNet)))
      ? parseFloat(aiNet)
      : (vatRate <= 0 ? brutto : (brutto > 0 ? Math.round((brutto / (1 + vatRate / 100)) * 100) / 100 : 0));
    // Feb 2026 — KI hat alle Werte extrahiert. tax_breakdown ist die Wahrheit.
    const extractedBreakdown = Array.isArray(d.tax_breakdown)
      ? d.tax_breakdown.filter(tb => tb && (parseFloat(tb.net) > 0 || parseFloat(tb.vat) > 0))
      : [];
    const isMulti = extractedBreakdown.length >= 2;
    setForm((f) => ({
      ...f,
      description: newData.description || f.description,
      amount: newData.amount || f.amount,
      date: newData.date || f.date,
      vendor: newData.vendor || f.vendor,
      invoice_number: newData.invoice_number || f.invoice_number,
      vat_rate: newData.vat_rate ?? f.vat_rate,
      category: newData.category || f.category,
      net_amount: netto,
      tax_breakdown: extractedBreakdown.length > 0 ? extractedBreakdown : f.tax_breakdown,
      multi_tax_suspected: isMulti,
      // Feb 2026 — Bewirtungsbescheinigung: Restaurant + Adresse aus Beleg auto-übernehmen
      hospitality_location: (newData.category === "hospitality" && d.hospitality_location)
        ? d.hospitality_location
        : f.hospitality_location,
      // Feb 2026 — FX-Conversion-Audit (nur gesetzt wenn Fremdwährung detektiert)
      original_amount: d.original_amount ?? f.original_amount,
      original_currency: d.original_currency ?? f.original_currency,
      fx_rate: d.fx_rate ?? f.fx_rate,
      fx_rate_date: d.fx_rate_date ?? f.fx_rate_date,
      fx_source: d.fx_source ?? f.fx_source,
    }));
    // Check for duplicates after data extraction
    checkDuplicateAfterOcr(newData);
  };

  const checkDuplicateAfterOcr = async (data) => {
    if (!data.amount || data.amount <= 0) return;
    try {
      const res = await api.post("/expenses/check-duplicate", {
        amount: parseFloat(data.amount),
        date: data.date ? new Date(data.date).toISOString() : undefined,
        vendor: data.vendor || undefined,
        invoice_number: data.invoice_number || undefined,
        exclude_id: expense?.id,
      });
      const matches = res.data.matches || [];
      if (matches.length > 0) {
        setDuplicateWarning({ open: true, matches, current: {
          description: data.description, vendor: data.vendor, amount: parseFloat(data.amount),
          date: data.date, invoice_number: data.invoice_number,
        } });
      }
    } catch (e) { /* ignore */ }
  };

  // Plan tier helpers
  const planTier = (() => {
    const p = userPlan || 'free';
    if (p.includes('pro') || p === 'lifetime') return 'pro';
    if (p.includes('basic')) return 'basic';
    return 'free';
  })();
  const canStoreReceipts = planTier !== 'free';
  const canUseAi = planTier === 'pro';

  const handleFileUpload = async (file) => {
    const allowed = [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".heif", ".webp"];
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) {
      toast.error("Dateityp nicht unterstützt");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Datei zu groß (max 25MB)");
      return;
    }

    setUploading(true);
    setLastUploadedFile(file);
    try {
      // Show thumbnail immediately
      if (file.type?.startsWith("image/")) {
        setReceiptPreviewUrl(URL.createObjectURL(file));
      } else {
        setReceiptPreviewUrl("pdf");
      }

      // Step 1: Store receipt file (Basic + Pro only)
      if (canStoreReceipts) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await api.post("/expenses/upload-receipt", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const receiptData = {
          receipt_file_key: res.data.file_key,
          receipt_filename: res.data.filename,
          receipt_content_type: res.data.content_type,
          storage_uri: res.data.storage_uri,
        };
        setForm((f) => ({ ...f, ...receiptData }));

        // Immediately persist receipt link to expense (don't wait for Save button)
        if (expense?.id) {
          try {
            await api.put(`/expenses/${expense.id}`, receiptData);
            onSaved?.(); // Refresh parent list so receipt_file_key is available on re-open
          } catch (e) {
            console.warn("Failed to auto-save receipt link:", e);
          }
        }
      } else {
        // Free: set filename for display but don't store
        setForm((f) => ({ ...f, receipt_filename: file.name }));
      }

      // Step 2: Extract data – Pro: KI, Free/Basic: OCR
      if (canUseAi && aiEnabled) {
        toast.success("Beleg hochgeladen – KI liest Daten aus...");
        setAnalyzing(true);
        try {
          const aiForm = new FormData();
          aiForm.append("file", file);
          const aiRes = await api.post("/expenses/analyze-receipt-ai", aiForm, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (aiRes.data?.extracted_data) {
            applyExtractedData(aiRes.data.extracted_data);
            setForm(f => ({ ...f, extraction_method: 'ai' }));
            toast.success(`KI-Erkennung abgeschlossen (${aiRes.data.extracted_data.confidence || 85}% Konfidenz)`);
          }
          // Use rendered image as PDF preview if available
          if (aiRes.data?.thumbnail) {
            setReceiptPreviewUrl(prev => prev === "pdf" ? aiRes.data.thumbnail : prev);
          }
        } catch (aiErr) {
          console.warn("AI failed, falling back to OCR:", aiErr);
          toast.info("KI nicht verfügbar – OCR wird verwendet...");
          setForm(f => ({ ...f, extraction_method: 'ocr' }));
          await runOcr(file);
        } finally {
          setAnalyzing(false);
        }
      } else {
        toast.success(canStoreReceipts
          ? "Beleg gespeichert – Daten werden ausgelesen..."
          : "Daten werden ausgelesen...");
        setForm(f => ({ ...f, extraction_method: 'ocr' }));
        await runOcr(file);
        if (!canStoreReceipts) {
          toast.info("Im Free-Plan werden Belege nicht gespeichert. Upgrade für Belegspeicherung.", { duration: 5000 });
        }
      }
    } catch (e) {
      toast.error("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const runOcr = async (file) => {
    try {
      const ocrForm = new FormData();
      ocrForm.append("file", file);
      const ocrRes = await api.post("/expenses/process-receipt", ocrForm, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (ocrRes.data?.status === "success") {
        if (ocrRes.data.extracted_data) {
          applyExtractedData(ocrRes.data.extracted_data);
          toast.success(`Daten erkannt (${ocrRes.data.extracted_data.confidence || 75}% Konfidenz)`);
        }
        // Use thumbnail from OCR response as PDF preview
        if (ocrRes.data.thumbnail) {
          setReceiptPreviewUrl(prev => prev === "pdf" ? ocrRes.data.thumbnail : prev);
        }
      }
    } catch (ocrErr) {
      if (ocrErr.response?.status !== 403) {
        console.warn("OCR failed:", ocrErr);
      }
    }
  };

  // Sep 2026 — gemeinsame Übernahme der KI-Extraktion ins Formular
  // (genutzt von Upload-Analyse UND „Neu auslesen").
  const applyAiResult = (d) => {
    // Feb 2026 — KI hat alle Werte extrahiert. tax_breakdown ist die Wahrheit.
    const extractedBreakdown = Array.isArray(d.tax_breakdown)
      ? d.tax_breakdown.filter(tb => tb && (parseFloat(tb.net) > 0 || parseFloat(tb.vat) > 0))
      : [];
    const isMulti = extractedBreakdown.length >= 2;
    // Feb 2026 — Backend liefert net_amount (nicht amount_net). RC/0% → net = gross.
    const aiNet = d.net_amount ?? d.amount_net ?? null;
    const vatR = parseFloat(d.vat_rate ?? 0) || 0;
    const grossA = parseFloat(d.amount ?? 0) || 0;
    const netComputed = (aiNet !== null && aiNet !== undefined && !isNaN(parseFloat(aiNet)))
      ? parseFloat(aiNet)
      : (vatR <= 0 ? grossA : (grossA > 0 ? Math.round((grossA / (1 + vatR / 100)) * 100) / 100 : 0));
    setForm((f) => ({
      ...f,
      description: d.description || f.description,
      amount: d.amount || f.amount,
      date: d.date || f.date,
      vendor: d.vendor || f.vendor,
      invoice_number: d.invoice_number || f.invoice_number,
      vat_rate: d.vat_rate ?? f.vat_rate,
      net_amount: netComputed,
      category: d.category_suggestion || f.category,
      tax_breakdown: extractedBreakdown.length > 0 ? extractedBreakdown : f.tax_breakdown,
      multi_tax_suspected: isMulti,
      extraction_method: 'ai',
      // Feb 2026 — Bewirtungsbescheinigung: Restaurant + Adresse aus Beleg auto-übernehmen
      hospitality_location: (d.category_suggestion === "hospitality" && d.hospitality_location)
        ? d.hospitality_location
        : f.hospitality_location,
      // Feb 2026 — FX-Conversion-Audit (nur gesetzt wenn Fremdwährung detektiert)
      original_amount: d.original_amount ?? f.original_amount,
      original_currency: d.original_currency ?? f.original_currency,
      fx_rate: d.fx_rate ?? f.fx_rate,
      fx_rate_date: d.fx_rate_date ?? f.fx_rate_date,
      fx_source: d.fx_source ?? f.fx_source,
    }));
    toast.success(`KI-Erkennung abgeschlossen (${d.confidence || 80}% Konfidenz)`);
  };

  const handleAiError = (e) => {
    const msg = e.response?.data?.detail || "KI-Analyse fehlgeschlagen";
    if (msg.includes("Pro-Plan")) {
      toast.error("KI-Erkennung erfordert den Pro-Plan");
    } else if (msg.includes("DSGVO") || msg.includes("deaktiviert")) {
      setShowConsentDialog(true);
    } else {
      toast.error(msg);
    }
  };

  const hasAiConsent = async () => {
    try {
      const consentRes = await api.get("/user/ai-consent");
      return !!consentRes.data.ai_consent;
    } catch (e) { return true; /* im Zweifel Backend entscheiden lassen */ }
  };

  const handleAiAnalysis = async () => {
    if (!lastUploadedFile) {
      toast.error("Bitte zuerst einen Beleg hochladen");
      return;
    }
    if (!(await hasAiConsent())) {
      setConsentAction("upload");
      setShowConsentDialog(true);
      return;
    }
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append("file", lastUploadedFile);
      const res = await api.post("/expenses/analyze-receipt-ai", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.extracted_data) applyAiResult(res.data.extracted_data);
    } catch (e) {
      handleAiError(e);
    } finally {
      setAnalyzing(false);
    }
  };

  // Sep 2026 — „Neu auslesen": gespeicherten Beleg erneut durch die KI schicken
  const handleReanalyzeAi = async () => {
    if (!expense?.id) return;
    if (!(await hasAiConsent())) {
      setConsentAction("reanalyze");
      setShowConsentDialog(true);
      return;
    }
    setAnalyzing(true);
    try {
      const res = await api.post(`/expenses/${expense.id}/reanalyze-ai`);
      if (res.data.extracted_data) {
        applyAiResult(res.data.extracted_data);
      } else {
        toast.error("KI konnte keine Daten aus dem Beleg extrahieren");
      }
    } catch (e) {
      handleAiError(e);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  // Juni 2026 — Schnellaktion (FL): fehlenden Satz als bewusst 0 % (steuerfrei) bestätigen.
  const confirmZeroVat = async () => {
    if (!expense?.id) return;
    setSaving(true);
    try {
      const gross = Number(form.amount) || Number(expense.amount) || 0;
      await api.put(`/expenses/${expense.id}`, { vat_rate: 0, vat_amount: 0, net_amount: gross });
      toast.success("Als 0 % (steuerfrei) bestätigt");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };


  const handleSave = async (skipDuplicateCheck = false, silent = false) => {
    if (!expense?.id) return;

    // Feb 2026 — Bei Multi-Tax: net_amount/vat_rate aus tax_breakdown rekonstruieren
    // (Single Source of Truth → keine Inkonsistenz zwischen den beiden Eingabe-Wegen).
    // Multi-Tax = tax_breakdown hat >= 2 echte Einträge (keine User-Entscheidung).
    const cleanBreakdown = Array.isArray(form.tax_breakdown)
      ? form.tax_breakdown
          .map(tb => ({
            rate: parseFloat(tb.rate) || 0,
            net: parseFloat(tb.net) || 0,
            vat: parseFloat(tb.vat) || 0,
          }))
          .filter(tb => tb.net > 0 || tb.vat > 0)
      : [];
    const isMultiTax = cleanBreakdown.length >= 2;

    let derivedNet = parseFloat(form.net_amount) || 0;
    let derivedVatRate = parseFloat(form.vat_rate) || 0;
    let derivedAmount = parseFloat(form.amount) || 0;
    if (isMultiTax) {
      const sumNet = cleanBreakdown.reduce((s, r) => s + r.net, 0);
      const sumVat = cleanBreakdown.reduce((s, r) => s + r.vat, 0);
      derivedNet = Math.round(sumNet * 100) / 100;
      // Brutto: bei Übereinstimmung mit form.amount lassen wir das oben Stehende
      // unverändert (kann Rundungsdiff haben). Bei großer Diff: Summe priorisieren.
      const sumGross = Math.round((sumNet + sumVat) * 100) / 100;
      if (Math.abs(sumGross - derivedAmount) > 0.05) {
        derivedAmount = sumGross;
      }
      // vat_rate auf Top-Level = Satz mit größtem Brutto (Gewinner-Position) — für DATEV-Fallback
      const winner = cleanBreakdown.reduce((a, b) => (a.net + a.vat) >= (b.net + b.vat) ? a : b);
      derivedVatRate = winner.rate;
    }

    const updateData = {
      description: form.description,
      amount: derivedAmount,
      date: form.date ? new Date(form.date).toISOString() : undefined,
      category: form.category,
      vat_rate: derivedVatRate,
      tax_deductible_type: form.tax_deductible_type,
      // Aug 2026 — 0 % ("Nicht absetzbar") darf nicht auf 100 zurückfallen
      deductible_percentage: (() => {
        const p = parseFloat(form.deductible_percentage);
        return Number.isFinite(p) ? Math.min(100, Math.max(0, p)) : 100;
      })(),
      // Aug 2026 (L1) — Privatanteil: kürzt Vorsteuer + Gewinn anteilig
      // Juli 2026 — Mutex: bei Bewirtung gilt nur die Absetzquote, kein Privatanteil
      private_use_percentage: (() => {
        if (form.category === "hospitality") return 0;
        const p = parseFloat(form.private_use_percentage);
        return Number.isFinite(p) ? Math.min(100, Math.max(0, p)) : 0;
      })(),
      // Juli 2026 (L2+L3) — Privatanteil-Modus + AT-PKW-Ausnahme
      private_use_mode: form.category === "hospitality" ? "anteilig" : (form.private_use_mode || "anteilig"),
      vehicle_vorsteuer_eligible: !!form.vehicle_vorsteuer_eligible,
      depreciation_type: form.depreciation_type || null,
      depreciation_years: form.depreciation_years ? parseInt(form.depreciation_years) : null,
      net_amount: derivedNet,
      vendor: form.vendor || null,
      invoice_number: form.invoice_number || null,
      notes: form.notes || null,
      receipt_file_key: canStoreReceipts ? (form.receipt_file_key || null) : null,
      receipt_filename: canStoreReceipts ? (form.receipt_filename || null) : null,
      receipt_content_type: canStoreReceipts ? (form.receipt_content_type || null) : null,
      storage_uri: canStoreReceipts ? (form.storage_uri || null) : null,
      extraction_method: form.extraction_method || null,
      // Feb 2026 — Multi-Tax-Aufschlüsselung
      tax_breakdown: isMultiTax ? cleanBreakdown : [],
      multi_tax_suspected: isMultiTax,
      // Feb 2026 — Bewirtungsbescheinigung (nur speichern wenn Kategorie passt)
      hospitality_occasion: form.category === "hospitality" ? (form.hospitality_occasion || null) : null,
      hospitality_participants: form.category === "hospitality" ? (form.hospitality_participants || null) : null,
      hospitality_location: form.category === "hospitality" ? (form.hospitality_location || null) : null,
      // Feb 2026 — Reverse-Charge / Innergem. Erwerb (Mutex zu Bewirtung — RC ist dort gesetzlich ausgeschlossen)
      is_reverse_charge: form.category === "hospitality" ? false : !!form.is_reverse_charge,
      is_intra_community_acquisition: form.category === "hospitality" ? false : !!form.is_intra_community_acquisition,
      rc_suspected: !!form.rc_suspected,
      supplier_country: form.supplier_country || null,
      supplier_vat_id: form.supplier_vat_id || null,
      // Juni 2026 — Vorsteuerpauschalierung (AT): Wareneinkauf/Fremdleistung-Sonder-VSt
      vstp_sonder_vorsteuer: !!form.vstp_sonder_vorsteuer,
      // Feb 2026 — FX-Conversion-Audit (Fremdwährungs-Belege § 256a HGB / § 16 UStG)
      original_amount: form.original_amount ?? null,
      original_currency: form.original_currency || null,
      fx_rate: form.fx_rate ?? null,
      fx_rate_date: form.fx_rate_date || null,
      fx_source: form.fx_source || null,
      // Juni 2026 — Wiederkehrende Ausgaben (nur für Vorlagen, nicht für Auto-Folgebuchungen)
      is_recurring: expense.is_recurring_occurrence ? false : !!form.is_recurring,
      recurrence_interval: (!expense.is_recurring_occurrence && form.is_recurring) ? (form.recurrence_interval || "monthly") : null,
      recurrence_custom_value: (!expense.is_recurring_occurrence && form.is_recurring && form.recurrence_interval === "custom") ? (parseInt(form.recurrence_custom_value) || 1) : null,
      recurrence_custom_unit: (!expense.is_recurring_occurrence && form.is_recurring && form.recurrence_interval === "custom") ? (form.recurrence_custom_unit || "months") : null,
      recurrence_end_date: (!expense.is_recurring_occurrence && form.is_recurring) ? (form.recurrence_end_date || null) : null,
    };

    // Duplicate check (only when amount or date changed)
    if (!skipDuplicateCheck) {
      const amountChanged = parseFloat(form.amount) !== parseFloat(expense.amount);
      const dateChanged = form.date !== (expense.date ? expense.date.split("T")[0] : "");
      if (amountChanged || dateChanged) {
        try {
          const res = await api.post("/expenses/check-duplicate", {
            amount: parseFloat(form.amount),
            date: form.date ? new Date(form.date).toISOString() : undefined,
            vendor: form.vendor || undefined,
            invoice_number: form.invoice_number || undefined,
            exclude_id: expense.id,
          });
          if ((res.data.matches || []).length > 0) {
            setDuplicateWarning({ open: true, matches: res.data.matches, current: {
              description: form.description, vendor: form.vendor, amount: parseFloat(form.amount),
              date: form.date, invoice_number: form.invoice_number,
            } });
            return;
          }
        } catch (e) { /* ignore check errors, proceed with save */ }
      }
    }

    setSaving(true);
    try {
      await api.put(`/expenses/${expense.id}`, updateData);
      setDidSave(true);
      // Dirty-Check-Snapshot aktualisieren (deckt manuellen Save + Duplikat-Continue ab)
      lastSavedFormRef.current = JSON.stringify(form);
      onSaved?.();
      if (!silent) {
        toast.success("Ausgabe gespeichert");
        onClose();
      }
    } catch (e) {
      if (!silent) toast.error("Speichern fehlgeschlagen");
      throw e;
    } finally {
      setSaving(false);
    }
  };
  // Ref immer auf die aktuelle handleSave-Closure (für Enter-zum-Speichern).
  handleSaveRef.current = handleSave;

  const allCategories = [
    ...categories,
  ];

  const renderDepreciationPlan = () => {
    if (form.depreciation_type !== "linear" || !form.depreciation_years || !netAmount) return null;
    const years = parseInt(form.depreciation_years) || 3;
    const annual = netAmount / years;
    const rows = [];
    let remaining = netAmount;
    // Use the expense date to determine the start year
    const startYear = form.date ? new Date(form.date).getFullYear() : new Date().getFullYear();
    for (let i = 0; i < years; i++) {
      remaining -= annual;
      rows.push({ year: startYear + i, amount: annual, remaining: Math.max(0, remaining) });
    }
    return (
      <div className="mt-3 border rounded-lg overflow-hidden" data-testid="depreciation-plan">
        <div className="bg-slate-50 px-3 py-2 text-sm font-medium flex items-center gap-2">
          <Calculator className="h-4 w-4" /> Abschreibungsplan (AfA)
        </div>
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-slate-50/50">
            <th className="text-left px-3 py-1.5">Jahr</th>
            <th className="text-right px-3 py-1.5">Abschreibung</th>
            <th className="text-right px-3 py-1.5">Restbuchwert</th>
          </tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.year} className="border-b last:border-0">
              <td className="px-3 py-1.5">{r.year}</td>
              <td className="text-right px-3 py-1.5">{r.amount.toFixed(2)} €</td>
              <td className="text-right px-3 py-1.5">{r.remaining.toFixed(2)} €</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  };

  if (!expense) return null;

  return (
    <>
    <Sheet open={isOpen} onOpenChange={(open) => {
      if (!open && receiptZoom) return;
      if (!open) handleClose();
    }}>
      <SheetContent side="right" className="w-full sm:max-w-[1040px] p-0 overflow-hidden" data-testid="expense-detail-sheet">
        <div className="flex h-full">
          {/* LEFT: persistente Belegvorschau + ↑/↓-Navigation (wie SB-Drawer) */}
          <div className="hidden lg:flex flex-col w-[44%] shrink-0 bg-zinc-100 border-r border-zinc-200" data-testid="expense-preview-pane">
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-white">
              <span className="text-xs font-medium text-slate-500">Belegvorschau</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => onPrev && onPrev()} disabled={!hasPrev}
                  className="p-1.5 rounded-md border border-zinc-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Vorherige Ausgabe (↑)" data-testid="expense-nav-prev">
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => onNext && onNext()} disabled={!hasNext}
                  className="p-1.5 rounded-md border border-zinc-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Nächste Ausgabe (↓)" data-testid="expense-nav-next">
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-3 flex items-start justify-center">
              {receiptKind === 'image' && receiptBlobUrl && (
                <img src={receiptBlobUrl} alt="Beleg-Vorschau"
                  onClick={() => setReceiptZoom(true)}
                  className="max-w-full max-h-full object-contain border border-zinc-200 rounded shadow bg-white cursor-zoom-in"
                  data-testid="expense-preview-image" />
              )}
              {receiptKind === 'pdf' && receiptBlobUrl && (
                <iframe src={receiptBlobUrl} title="Beleg-Vorschau"
                  className="w-full h-full min-h-[70vh] border border-zinc-200 rounded bg-white"
                  data-testid="expense-preview-pdf" />
              )}
              {receiptKind === 'file' && (
                <button type="button"
                  onClick={async () => {
                    try {
                      const res = await api.get(`/expenses/${expense.id}/receipt`, { responseType: 'blob' });
                      const url = URL.createObjectURL(res.data);
                      window.open(url, '_blank');
                    } catch { toast.error('Beleg konnte nicht geladen werden'); }
                  }}
                  className="mt-12 flex flex-col items-center gap-2 text-slate-500 hover:text-slate-700">
                  <FileText className="w-12 h-12" />
                  <span className="text-xs flex items-center gap-1"><Download className="w-3 h-3" /> Beleg herunterladen</span>
                </button>
              )}
              {!receiptBlobUrl && receiptKind !== 'file' && (
                <div className="mt-16 flex flex-col items-center gap-2 text-slate-400 text-center px-4">
                  <FileText className="w-12 h-12" />
                  <span className="text-xs">Kein Beleg hochgeladen.<br />Rechts ablegen oder hochladen.</span>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: scrollbare Detail-/Bearbeiten-Spalte */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
        <SheetHeader>
          <SheetTitle className="text-left">
            {expense?.description === "Neue Ausgabe" && expense?.amount === 0 ? "Neue Ausgabe erfassen" : "Ausgabe bearbeiten"}
          </SheetTitle>
        </SheetHeader>

        {/* Advisor modification banner */}
        {expense?.modified_by_advisor && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 mt-2" data-testid="advisor-modified-banner">
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>
            <span>Zuletzt geändert von <strong>{expense.modified_by_advisor_name || 'Steuerberater'}</strong>
            {expense.modified_by_advisor_at && ` am ${new Date(expense.modified_by_advisor_at).toLocaleDateString('de-DE')}`}
            </span>
          </div>
        )}

        {expense?.deactivated_at && (
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-800 mt-2" data-testid="expense-deactivated-banner">
            <svg className="w-4 h-4 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
            <span>
              <strong>Deaktivierte Ausgabe</strong> — zählt nicht in UVA, Gewinn (EÜR), Periodenabschluss oder Export.
              {expense.deactivated_reason ? <> Grund: „{expense.deactivated_reason}".</> : null}
              {(expense.deactivated_by_name || expense.deactivated_at) && (
                <span className="block text-amber-600 mt-0.5">
                  {expense.deactivated_by_name ? `${expense.deactivated_by_name} · ` : ''}
                  {expense.deactivated_at ? new Date(expense.deactivated_at).toLocaleString('de-DE') : ''}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Juli 2026 — Beleg-Eingang per E-Mail: Herkunft + Bestätigen-Aktion */}
        {(expense?.source === 'email_inbox' || expense?.source === 'mobile_scan') && (
          <div className="px-3 py-2 bg-sky-50 border border-sky-200 rounded text-xs text-sky-800 mt-2 space-y-1.5" data-testid="expense-inbound-banner">
            <div className="flex items-start gap-2">
              {expense.source === 'mobile_scan' ? <Smartphone className="w-4 h-4 shrink-0 mt-0.5" /> : <Mail className="w-4 h-4 shrink-0 mt-0.5" />}
              <span className="min-w-0">
                <strong>{expense.source === 'mobile_scan' ? 'Per Smartphone gescannt (KI-Auslese)' : 'Per E-Mail eingegangen'}</strong>
                {expense.inbound_meta?.from && <> von <span className="font-medium break-all">{expense.inbound_meta.from}</span></>}
                {expense.inbound_meta?.received_at && <> am {new Date(expense.inbound_meta.received_at).toLocaleString('de-DE')}</>}
                {expense.inbound_meta?.subject && <span className="block text-sky-600 truncate">Betreff: „{expense.inbound_meta.subject}"</span>}
              </span>
            </div>
            {expense.einvoice_format && (
              <div className="flex items-start gap-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-emerald-800" data-testid="expense-einvoice-hint">
                <CheckCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span><strong>Strukturierte E-Rechnung</strong> ({expense.einvoice_format === 'zugferd' ? 'ZUGFeRD/Factur-X' : 'XRechnung'}) — Beträge, USt und Lieferant wurden exakt aus dem Rechnungs-XML übernommen, keine KI-Schätzung.{expense.einvoice_credit_note && <> <strong>Achtung: Gutschrift.</strong></>}</span>
              </div>
            )}
            {expense.body_derived && (
              <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-800" data-testid="expense-body-derived-hint">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Dieser Beleg wurde aus dem <strong>E-Mail-Text</strong> erzeugt — <strong>kein Originalbeleg</strong>. Bitte Angaben prüfen und nach Möglichkeit den Original-Beleg (PDF) nachreichen.</span>
              </div>
            )}
            {expense.inbox_status === 'new' && expense.date && !String(expense.date).startsWith(String(new Date().getFullYear())) && (
              <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-800" data-testid="expense-offyear-hint">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Das erkannte <strong>Belegdatum liegt nicht im aktuellen Jahr</strong>. Bitte prüfen: KI-Lesefehler oder tatsächlich ein alter Beleg? Ein falsches Jahr verschiebt die Ausgabe in eine andere Steuerperiode.</span>
              </div>
            )}
            {(inboxConfirmed || expense.inbox_status === 'confirmed') ? (
              <div className="flex items-center gap-1.5 text-emerald-700" data-testid="expense-inbound-confirmed">
                <CheckCircle className="w-3.5 h-3.5" /> Geprüft & bestätigt
              </div>
            ) : (
              /* Sep 2026 — Buttons konsolidiert: Bestätigung läuft über den
                 „Als geprüft markieren"-Button am Drawer-Ende (setzt beides). */
              <div className="flex items-center gap-1.5 text-sky-700" data-testid="expense-inbound-pending-hint">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span>Noch nicht bestätigt — unten <strong>„Als geprüft markieren"</strong> klicken.</span>
              </div>
            )}
          </div>
        )}

        {/* Juli 2026 — Duplikat-Workflow: Vergleich + Entscheidung (E-Mail-Eingang UND Upload) */}
        <DuplicateReviewPanel
          expense={expense}
          onResolved={() => onSaved?.()}
          onDeactivated={() => { onSaved?.(); onClose?.(); }}
        />

        <div className="space-y-5 mt-4">
          {/* Receipt Upload / Drop Zone */}
          <div
            data-testid="receipt-drop-zone"
            className={`border-2 border-dashed rounded-lg transition-colors ${
              dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-slate-400"
            } ${receiptPreviewUrl && !uploading ? "p-2.5" : "p-4"} ${!receiptPreviewUrl && !uploading ? "cursor-pointer text-center" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => {
              if (!receiptPreviewUrl && !uploading) {
                document.getElementById("receipt-file-input")?.click();
              }
            }}
          >
            <input
              id="receipt-file-input"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
              capture="environment"
              className="hidden"
              onChange={(e) => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); }}
            />

            {uploading ? (
              /* Uploading state */
              <div className="flex flex-col items-center gap-2 text-slate-500">
                <Loader2 className="h-8 w-8 animate-spin" />
                <p className="text-sm">Wird hochgeladen und ausgelesen...</p>
              </div>
            ) : receiptPreviewUrl ? (
              /* Beleg vorhanden – kompakte Zeile (große Vorschau lebt links) */
              <div className="flex items-center gap-2 min-w-0" data-testid="receipt-preview-area">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="text-sm font-medium text-slate-700 flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="truncate">{form.receipt_filename || "Beleg"}</span>
                  {analyzing && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 shrink-0" />}
                  {(form.extraction_method || expense?.extraction_method) && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium shrink-0 ${
                      (form.extraction_method || expense?.extraction_method) === 'ai'
                        ? 'bg-violet-50 text-violet-700 border-violet-200'
                        : 'bg-slate-100 text-slate-600 border-slate-200'
                    }`} data-testid="extraction-method-badge">
                      {(form.extraction_method || expense?.extraction_method) === 'ai' ? 'KI' : 'OCR'}
                    </span>
                  )}
                  {/* Aug 2026 — Grund für OCR-Fallback transparent machen */}
                  {expense?.ai_fallback_reason && (form.extraction_method || expense?.extraction_method) !== 'ai' && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded border font-medium shrink-0 bg-amber-50 text-amber-700 border-amber-200"
                      data-testid="ai-fallback-reason-badge"
                      title={
                        expense.ai_fallback_reason === 'quota_exceeded'
                          ? 'KI-Monatslimit war beim Upload erreicht — Beleg wurde per OCR verarbeitet.'
                          : expense.ai_fallback_reason === 'no_consent'
                            ? 'KI-Einwilligung war beim Empfang nicht aktiv.'
                            : 'KI-Dienst war beim Upload nicht erreichbar (z. B. Rate-Limit) — Beleg wurde per OCR verarbeitet.'
                      }
                    >
                      {expense.ai_fallback_reason === 'quota_exceeded' ? 'KI-Limit erreicht'
                        : expense.ai_fallback_reason === 'no_consent' ? 'KI war deaktiviert'
                        : 'KI-Fehler beim Upload'}
                    </span>
                  )}
                </span>
                {!canStoreReceipts && (
                  <span className="text-[11px] text-amber-600 shrink-0">nicht gespeichert (Free)</span>
                )}
                {form.receipt_file_key && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs shrink-0"
                    type="button"
                    title="Beleg herunterladen"
                    onClick={async () => {
                      try {
                        const res = await api.get(`/expenses/${expense.id}/receipt`, { responseType: 'blob' });
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(res.data);
                        a.download = form.receipt_filename || 'beleg';
                        a.click();
                      } catch { toast.error('Download fehlgeschlagen'); }
                    }}
                    data-testid="receipt-download-btn"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                )}
                {form.receipt_file_key && expense?.id && !expense?.is_locked && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs shrink-0 border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                    type="button"
                    disabled={analyzing}
                    title="Beleg erneut durch die KI-Extraktion laufen lassen"
                    onClick={handleReanalyzeAi}
                    data-testid="receipt-reanalyze-ai-btn"
                  >
                    {analyzing
                      ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      : <Sparkles className="h-3 w-3 mr-1" />}
                    Neu auslesen
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs shrink-0"
                  type="button"
                  onClick={() => document.getElementById("receipt-file-input")?.click()}
                  data-testid="receipt-replace-btn"
                >
                  <Upload className="h-3 w-3 mr-1" /> Ersetzen
                </Button>
              </div>
            ) : (
              /* Empty state – drop zone */
              <div className="text-slate-500">
                <Upload className="h-8 w-8 mx-auto mb-1 opacity-50" />
                <p className="text-sm">Beleg hierher ziehen oder klicken</p>
                <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, HEIC (max 25MB)</p>
                <Button size="sm" variant="outline" className="mt-2" type="button" data-testid="receipt-choose-file-btn">
                  <Upload className="h-3.5 w-3.5 mr-1" /> Datei auswählen
                </Button>
                {/* Sep 2026 — Fotografieren geht nur am Smartphone: QR-Code als Popover direkt im Drawer */}
                <div className="mt-2.5 flex justify-center" onClick={(e) => e.stopPropagation()}>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-emerald-700 underline-offset-2 hover:underline"
                        data-testid="receipt-mobile-scan-link"
                        title="QR-Code für den Smartphone-Scanner anzeigen"
                      >
                        <Smartphone className="h-3 w-3" />
                        Beleg fotografieren? Mit dem Smartphone scannen
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="center" className="w-64 p-4 text-center" data-testid="receipt-mobile-scan-popover">
                      <div className="inline-block rounded-lg border border-slate-200 bg-white p-2">
                        <QRCodeSVG value={`${window.location.origin}/m/scan`} size={168} data-testid="receipt-mobile-scan-qr" />
                      </div>
                      <p className="mt-3 text-xs font-semibold text-slate-800">Mit dem Handy scannen</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                        Öffnet den Beleg-Scanner mit Kamera, Auto-Zuschnitt und KI-Auslese. Der Beleg landet direkt in deinen Ausgaben.
                      </p>
                      <Link
                        to="/m/scan"
                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:underline"
                        data-testid="receipt-mobile-scan-open-link"
                      >
                        <ExternalLink className="h-3 w-3" /> Scanner-Seite öffnen
                      </Link>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Basic Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Beschreibung</Label>
              <Input
                data-testid="expense-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div>
              <Label>Betrag (brutto)</Label>
              <CurrencyInput
                data-testid="expense-amount"
                country={userCountry}
                value={form.amount}
                onChange={(num) => handleAmountChange(num ?? 0)}
                label={null}
              />
            </div>
            <div>
              <Label>Datum</Label>
              <Input
                data-testid="expense-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Händler / Lieferant</Label>
              <Input
                data-testid="expense-vendor"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                placeholder="z.B. Amazon, IKEA"
              />
            </div>
            <div>
              <Label>Rechnungsnummer</Label>
              <Input
                data-testid="expense-invoice-number"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <Label>Kategorie</Label>
            <Select
              value={form.category}
              onValueChange={(v) => {
                // Feb 2026 — Bei Kategorie-Wechsel auf Bewirtung: §4 EStG-Regel auto-vorbelegen
                // DE: 70% absetzbar · AT: 50% absetzbar · CH: 100%
                const country = (userCountry || "DE").toUpperCase();
                const hospitalityPct = country === "AT" ? 50 : country === "CH" ? 100 : 70;
                if (v === "hospitality") {
                  setForm((f) => ({
                    ...f,
                    category: v,
                    tax_deductible_type: country === "CH" ? "full" : "partial",
                    deductible_percentage: hospitalityPct,
                  }));
                  // Lazy-load Recent-Teilnehmer + Locations beim Kategorie-Wechsel
                  if (recentParticipants.length === 0) {
                    api.get("/expenses/hospitality-recent-participants")
                      .then((r) => setRecentParticipants(r.data?.participants || []))
                      .catch(() => {});
                  }
                  if (recentLocations.length === 0) {
                    api.get("/expenses/hospitality-recent-locations")
                      .then((r) => setRecentLocations(r.data?.locations || []))
                      .catch(() => {});
                  }
                } else if (form.category === "hospitality" && v !== "hospitality") {
                  // Verlässt Bewirtung → Standard zurück + Felder leeren
                  setForm((f) => ({
                    ...f,
                    category: v,
                    tax_deductible_type: "full",
                    deductible_percentage: 100,
                    hospitality_occasion: "",
                    hospitality_participants: "",
                    hospitality_location: "",
                  }));
                } else if (v === "telecom" && Number(form.private_use_percentage) === 0) {
                  // Juli 2026 (L5) — Telefon/Internet: üblichen Privatanteil vorbelegen (AT 40 % / DE 30 %)
                  setForm((f) => ({
                    ...f,
                    category: v,
                    private_use_percentage: country === "AT" ? 40 : country === "CH" ? 0 : 30,
                  }));
                } else {
                  setForm((f) => ({ ...f, category: v }));
                }
              }}
            >
              <SelectTrigger data-testid="expense-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allCategories.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label_de}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Juli 2026 (L3) — AT: PKW ohne Vorsteuerabzug (§ 12 Abs. 2 Z 2 lit. b UStG) */}
          {(userCountry || "").toUpperCase() === "AT" && form.category === "vehicle" && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="vehicle-vorsteuer-card">
              <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                PKW-/Kombi-Kosten: in Österreich kein Vorsteuerabzug
              </div>
              <p className="text-[11px] text-amber-800 mt-1 leading-relaxed">
                § 12 Abs. 2 Z 2 lit. b UStG — die USt dieses Belegs fließt nicht in die UVA (KZ 060),
                das Brutto zählt als Betriebsausgabe. Ausnahmen: E-Auto (0 g CO₂), Taxi, Fahrschule,
                Kleinbus/Fiskal-LKW.
              </p>
              <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="vehicle-vorsteuer-eligible"
                  className="h-4 w-4 rounded border-amber-300 accent-amber-700"
                  checked={!!form.vehicle_vorsteuer_eligible}
                  onChange={(e) => setForm({ ...form, vehicle_vorsteuer_eligible: e.target.checked })}
                />
                Ausnahme trifft zu — Vorsteuerabzug zulässig
              </label>
            </div>
          )}

          {/* Feb 2026 — FX-Conversion-Audit-Anzeige (Fremdwährungs-Belege gem. § 256a HGB / § 16 UStG) */}
          {form.original_currency && form.original_currency !== (userCountry === 'CH' ? 'CHF' : 'EUR') && form.original_amount && (
            <div
              className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3"
              data-testid="expense-fx-audit-card"
            >
              <div className="text-xs font-semibold text-emerald-900 flex items-center gap-1.5">
                <span aria-hidden>💱</span>
                <span>Fremdwährungs-Beleg — automatisch umgerechnet</span>
              </div>
              <div className="text-[11px] text-emerald-800 mt-1.5 leading-relaxed space-y-0.5">
                <div>
                  Original: <strong>{Number(form.original_amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {form.original_currency}</strong>
                </div>
                {form.fx_rate && form.fx_rate_date && (
                  <div>
                    EZB-Kurs vom <strong>{form.fx_rate_date}</strong>: 1 {form.original_currency} = <strong>{Number(form.fx_rate).toFixed(4)} {userCountry === 'CH' ? 'CHF' : 'EUR'}</strong>
                  </div>
                )}
                <div>
                  Verbucht: <strong>{Number(form.amount || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {userCountry === 'CH' ? 'CHF' : 'EUR'}</strong>
                  {form.fx_source && (
                    <span className="ml-1 text-emerald-600">({form.fx_source === 'frankfurter_live' ? 'Live-Kurs' : form.fx_source === 'frankfurter_cache' ? 'Cache' : form.fx_source === 'frankfurter_fallback_today' ? 'Tageskurs (Beleg-Datum nicht verfügbar)' : form.fx_source})</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Aug 2026 — Drittland-Steuer: kein Vorsteuerabzug (Sales Tax / OSS-USt) */}
          {expense?.foreign_vat_blocked && (
            <div className="mt-3 rounded-lg border border-orange-300 bg-orange-50 p-3" data-testid="expense-foreign-vat-blocked">
              <div className="text-xs font-semibold text-orange-900 flex items-center gap-1.5">
                <span aria-hidden>⚠️</span>
                <span>Ausländische Steuer — kein Vorsteuerabzug</span>
              </div>
              <div className="text-[11px] text-orange-800 mt-1.5 leading-relaxed">
                Dieser Drittland-Beleg weist eine Steuer aus
                {expense?.foreign_tax_amount ? (
                  <> (<strong>{Number(expense.foreign_tax_amount).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {userCountry === 'CH' ? 'CHF' : '€'}</strong>)</>
                ) : null}
                . Sie ist nicht als Vorsteuer abziehbar und wurde als Kostenbestandteil verbucht
                (Brutto = Betriebsausgabe, UVA unberührt).
                <br />
                <strong>Tipp:</strong> Hinterlege deine UID beim Anbieter — dann erhältst du Netto-Rechnungen (Reverse Charge).
              </div>
            </div>
          )}

          {/* Feb 2026 — Reverse-Charge / Innergem. Erwerb (Eingangsrechnungen).
              Mutex zu Bewirtung — RC ist gesetzlich ausgeschlossen bei Bewirtung. */}
          {form.category !== "hospitality" && (() => {
            const isStandard = !form.is_reverse_charge && !form.is_intra_community_acquisition;
            const isRC = !!form.is_reverse_charge;
            const isIGE = !!form.is_intra_community_acquisition;
            const showSuspectBanner = form.rc_suspected && isStandard;
            return (
              <div
                className={`mt-3 rounded-lg border ${showSuspectBanner ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"} p-3`}
                data-testid="expense-tax-scheme-card"
              >
                {showSuspectBanner && (
                  <div className="text-xs text-amber-900 mb-2 flex items-start gap-1.5">
                    <span aria-hidden>⚠️</span>
                    <span>
                      Die Beleg-Analyse vermutet, dass dies eine <strong>Reverse-Charge-Leistung</strong> oder ein <strong>Innergem. Erwerb</strong> ist
                      (EU-Lieferant, keine USt ausgewiesen). Bitte unten bestätigen oder verwerfen.
                    </span>
                  </div>
                )}
                <div className="text-xs font-semibold text-slate-700 mb-2">Steuerart</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <label className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer text-xs ${isStandard ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <input
                      type="radio"
                      checked={isStandard}
                      onChange={() => setForm(f => ({ ...f, is_reverse_charge: false, is_intra_community_acquisition: false }))}
                      data-testid="tax-scheme-standard"
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-slate-800">Standard</div>
                      <div className="text-slate-500 mt-0.5">USt aus Brutto und Satz berechnet</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer text-xs ${isRC ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <input
                      type="radio"
                      checked={isRC}
                      onChange={() => setForm(f => ({
                        ...f,
                        is_reverse_charge: true,
                        is_intra_community_acquisition: false,
                        vat_rate: 0,
                        // Feb 2026 — bei RC: net = gross (keine USt im Brutto enthalten)
                        net_amount: parseFloat(f.amount) || 0,
                        // Multi-Tax-Aufschlüsselung deaktivieren (RC kennt keine Split-Sätze)
                        tax_breakdown: [],
                        multi_tax_suspected: false,
                      }))}
                      data-testid="tax-scheme-rc"
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-slate-800">
                        Reverse Charge
                        <FachInfo
                          title="Reverse Charge"
                          testId="fach-info-rc"
                          law="§ 13b UStG (DE) / § 19 UStG (AT)"
                        >
                          Bei Leistungen ausländischer Anbieter geht die Steuerschuld auf dich als
                          Empfänger über: Die Rechnung kommt ohne USt, du meldest die Steuer selbst
                          an und ziehst sie i. d. R. gleichzeitig als Vorsteuer — unterm Strich
                          meist ein Nullsummenspiel in der Voranmeldung.
                        </FachInfo>
                      </div>
                      <div className="text-slate-500 mt-0.5">Empfänger schuldet die Steuer (§ 13b / § 19)</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer text-xs ${isIGE ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <input
                      type="radio"
                      checked={isIGE}
                      onChange={() => setForm(f => ({
                        ...f,
                        is_reverse_charge: false,
                        is_intra_community_acquisition: true,
                        vat_rate: 0,
                        // Feb 2026 — bei Innergem. Erwerb: net = gross (keine UStG im Brutto)
                        net_amount: parseFloat(f.amount) || 0,
                        tax_breakdown: [],
                        multi_tax_suspected: false,
                      }))}
                      data-testid="tax-scheme-intra-eu"
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-slate-800">
                        Innergem. Erwerb
                        <FachInfo
                          title="Innergemeinschaftlicher Erwerb"
                          testId="fach-info-ige"
                          law="§ 1a UStG (DE) / Art. 1 UStG (AT)"
                        >
                          Warenkauf aus einem anderen EU-Land mit deiner UID: Der Lieferant stellt
                          netto, du versteuerst den Erwerb im Inland und ziehst die Erwerbsteuer
                          zugleich als Vorsteuer — saldenneutral, muss aber gemeldet werden.
                        </FachInfo>
                      </div>
                      <div className="text-slate-500 mt-0.5">EU-Warenlieferung mit eigener UID</div>
                    </div>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-200">
                    <div>
                      <Label className="text-xs text-slate-600 mb-1 block">Land des Lieferanten</Label>
                      <Input
                        value={form.supplier_country || ""}
                        onChange={e => setForm(f => ({ ...f, supplier_country: e.target.value.toUpperCase().slice(0, 2) }))}
                        placeholder="z.B. IT, FR, NL"
                        maxLength={2}
                        className="h-8 text-sm"
                        data-testid="rc-supplier-country"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600 mb-1 block">USt-IdNr. des Lieferanten</Label>
                      <Input
                        value={form.supplier_vat_id || ""}
                        onChange={e => setForm(f => ({ ...f, supplier_vat_id: e.target.value.toUpperCase().trim() }))}
                        placeholder="z.B. ATU12345678"
                        className="h-8 text-sm"
                        data-testid="rc-supplier-vat-id"
                      />
                    </div>
                    {form.rc_auto_confirmed && isRC && (
                      <div className="md:col-span-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 leading-relaxed" data-testid="rc-auto-confirmed-note">
                        ✅ Automatisch als <strong>Reverse Charge</strong> erkannt: Lieferant im Ausland ({form.supplier_country || "—"}), USt-IdNr. vorhanden, 0 % USt. Bei Warenlieferung bitte auf „Innergem. Erwerb" umstellen.
                      </div>
                    )}
                    {(isRC || isIGE) && (
                      <div className="md:col-span-2 text-[11px] text-slate-500 leading-relaxed">
                        ℹ️ Bei {isRC ? "Reverse Charge" : "Innergem. Erwerb"} wird automatisch <strong>0 % USt</strong> gesetzt — der Betrag fließt korrekt in die Voranmeldung (Vorsteuer + Steuerschuld als Empfänger).
                      </div>
                    )}
                    {isStandard && (() => {
                      const uc = (userCountry || "DE").toUpperCase();
                      const sc = (form.supplier_country || "").toUpperCase();
                      const rcSuggested = sc && sc !== uc && (form.supplier_vat_id || "").trim() && (parseFloat(form.vat_rate) || 0) === 0;
                      if (rcSuggested) {
                        return (
                          <div className="md:col-span-2 flex items-start justify-between gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed" data-testid="rc-suggestion-banner">
                            <span>Ausländische UID ({sc}) + 0 % USt erkannt — das ist meist ein <strong>Reverse-Charge</strong>-Fall.</span>
                            <button
                              type="button"
                              onClick={() => setForm(f => ({
                                ...f,
                                is_reverse_charge: true,
                                is_intra_community_acquisition: false,
                                vat_rate: 0,
                                net_amount: parseFloat(f.amount) || 0,
                                tax_breakdown: [],
                                multi_tax_suspected: false,
                              }))}
                              className="shrink-0 rounded-md bg-amber-600 text-white px-2 py-1 text-[11px] font-medium hover:bg-amber-700 transition-colors"
                              data-testid="rc-suggestion-apply-btn"
                            >
                              Als Reverse Charge übernehmen
                            </button>
                          </div>
                        );
                      }
                      if (form.supplier_country || form.supplier_vat_id) {
                        return (
                          <div className="md:col-span-2 text-[11px] text-slate-500 leading-relaxed">
                            Aus dem Beleg ausgelesen. Bei ausländischer UID + 0 % USt liegt meist ein <strong>Reverse-Charge</strong>-Fall vor — dann oben „Reverse Charge" wählen.
                          </div>
                        );
                      }
                      return null;
                    })()}
                </div>
              </div>
            );
          })()}

          {/* Juni 2026 — Vorsteuerpauschalierung (AT): Wareneinkauf/Fremdleistung markieren.
              Nur AT, nur reguläre Belege (keine RC/IGE, keine Bewirtung). */}
          {vstpActive && (userCountry || "").toUpperCase() === "AT"
            && form.category !== "hospitality"
            && !form.is_reverse_charge
            && !form.is_intra_community_acquisition && (
            <label
              className={`mt-3 flex items-start gap-2 rounded-lg border p-3 cursor-pointer text-xs ${form.vstp_sonder_vorsteuer ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              data-testid="vstp-sonder-vorsteuer-toggle"
            >
              <input
                type="checkbox"
                checked={!!form.vstp_sonder_vorsteuer}
                onChange={(e) => setForm(f => ({ ...f, vstp_sonder_vorsteuer: e.target.checked }))}
                className="mt-0.5"
                data-testid="vstp-sonder-vorsteuer-checkbox"
              />
              <div>
                <div className="font-medium text-slate-800">Wareneinkauf / Fremdleistung (Vorsteuerpauschalierung)</div>
                <div className="text-slate-500 mt-0.5">
                  Vorsteuer dieses Belegs ist bei aktiver Pauschalierung <strong>zusätzlich</strong> zur Pauschale abziehbar
                  (Roh-/Hilfsstoffe, Handelswaren, Fremdlöhne — § 14 UStG / § 7 VO).
                </div>
              </div>
            </label>
          )}
          {form.category === "hospitality" && (() => {
            const country = (userCountry || "DE").toUpperCase();
            const hospitalityPct = country === "AT" ? 50 : country === "CH" ? 100 : 70;
            const ruleText = country === "AT"
              ? "§ 20 Abs. 1 Z 3 EStG (AT) — Bewirtungskosten zu 50 % absetzbar"
              : country === "CH"
                ? "Bewirtung: 100 % als Geschäftsaufwand absetzbar"
                : "§ 4 Abs. 5 Nr. 2 EStG (DE) — Bewirtungskosten zu 70 % absetzbar";
            const isOccasionMissing = !form.hospitality_occasion?.trim();
            const isParticipantsMissing = !form.hospitality_participants?.trim();
            const isLocationMissing = !form.hospitality_location?.trim();
            const anyMissing = isOccasionMissing || isParticipantsMissing || isLocationMissing;
            return (
              <div
                className={`rounded-xl border-2 p-4 space-y-3 ${
                  anyMissing ? "border-amber-300 bg-amber-50/60" : "border-emerald-200 bg-emerald-50/40"
                }`}
                data-testid="hospitality-receipt-card"
              >
                <div className="flex items-start gap-2">
                  {anyMissing ? (
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  ) : (
                    <span className="text-emerald-600 text-base shrink-0 mt-0.5">✓</span>
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${anyMissing ? "text-amber-900" : "text-emerald-900"}`}>
                      Bewirtungsbescheinigung {anyMissing ? "— unvollständig" : "— vollständig"}
                    </p>
                    <p className={`text-xs mt-0.5 ${anyMissing ? "text-amber-800" : "text-emerald-800"}`}>
                      {ruleText}. Pflichtangaben für die steuerliche Anerkennung.
                    </p>
                  </div>
                </div>

                {/* Pflichtfelder */}
                <div className="space-y-2.5">
                  <div>
                    <Label className="text-xs text-slate-700">
                      Anlass der Bewirtung <span className="text-rose-600">*</span>
                    </Label>
                    <Input
                      data-testid="hospitality-occasion"
                      placeholder="z.B. Projektbesprechung mit Kunde Müller (Angebotsverhandlung)"
                      value={form.hospitality_occasion || ""}
                      onChange={(e) => setForm({ ...form, hospitality_occasion: e.target.value })}
                      className={`h-9 text-sm ${isOccasionMissing ? "border-rose-300 focus-visible:ring-rose-300" : ""}`}
                    />
                    {isOccasionMissing && (
                      <p className="text-[11px] text-rose-700 mt-1">
                        Bitte präzisen geschäftlichen Grund angeben (allgemein „Geschäftsessen" reicht nicht).
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs text-slate-700">
                      Teilnehmer <span className="text-rose-600">*</span>
                    </Label>
                    <Textarea
                      data-testid="hospitality-participants"
                      placeholder={"Vollständige Namen, eine Person pro Zeile:\nMax Mustermann (eigene Person)\nAnna Müller (Müller GmbH, Geschäftsführerin)"}
                      value={form.hospitality_participants || ""}
                      onChange={(e) => setForm({ ...form, hospitality_participants: e.target.value })}
                      rows={3}
                      className={`text-sm ${isParticipantsMissing ? "border-rose-300 focus-visible:ring-rose-300" : ""}`}
                    />
                    {/* Feb 2026 — Recent Teilnehmer Quick-Pick (nur wenn welche da sind und Textarea nicht voll) */}
                    {recentParticipants.length > 0 && (
                      <div className="mt-1.5" data-testid="hospitality-recent-pick">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                          Häufig genutzt — Klick fügt hinzu
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {recentParticipants.slice(0, 5).map((p) => {
                            const alreadyInText = (form.hospitality_participants || "")
                              .toLowerCase()
                              .includes(p.name.toLowerCase());
                            return (
                              <button
                                key={p.name}
                                type="button"
                                disabled={alreadyInText}
                                onClick={() => {
                                  const cur = (form.hospitality_participants || "").trim();
                                  const sep = cur ? "\n" : "";
                                  setForm((f) => ({
                                    ...f,
                                    hospitality_participants: cur + sep + p.name,
                                  }));
                                }}
                                className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                                  alreadyInText
                                    ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                }`}
                                data-testid={`hospitality-recent-${p.name.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}`}
                                title={`${p.count}× verwendet`}
                              >
                                {alreadyInText ? "✓ " : "+ "}{p.name}
                                {p.count > 1 && (
                                  <span className="ml-1 text-[9px] opacity-70">·{p.count}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {isParticipantsMissing && (
                      <p className="text-[11px] text-rose-700 mt-1">
                        Alle Teilnehmer inkl. eigener Person mit vollständigem Namen — Pflicht laut §4 EStG.
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs text-slate-700">
                      Ort der Bewirtung <span className="text-rose-600">*</span>
                    </Label>
                    <Input
                      data-testid="hospitality-location"
                      placeholder="z.B. Restaurant Adler, Hauptstraße 12, 10115 Berlin"
                      value={form.hospitality_location || ""}
                      onChange={(e) => setForm({ ...form, hospitality_location: e.target.value })}
                      className={`h-9 text-sm ${isLocationMissing ? "border-rose-300 focus-visible:ring-rose-300" : ""}`}
                    />
                    {/* Feb 2026 — Recent Locations Quick-Pick */}
                    {recentLocations.length > 0 && (
                      <div className="mt-1.5" data-testid="hospitality-location-recent-pick">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                          Häufig genutzt — Klick übernimmt
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {recentLocations.slice(0, 4).map((l) => {
                            const isCurrent = (form.hospitality_location || "").trim() === l.name.trim();
                            return (
                              <button
                                key={l.name}
                                type="button"
                                disabled={isCurrent}
                                onClick={() => setForm((f) => ({ ...f, hospitality_location: l.name }))}
                                className={`text-[11px] px-2 py-0.5 rounded-full border transition truncate max-w-[260px] ${
                                  isCurrent
                                    ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                                }`}
                                title={l.name + (l.count > 1 ? ` (${l.count}× verwendet)` : "")}
                                data-testid={`hospitality-location-recent-${l.name.replace(/\s+/g, "-").toLowerCase().slice(0, 30)}`}
                              >
                                {isCurrent ? "✓ " : ""}{l.name}
                                {l.count > 1 && (
                                  <span className="ml-1 text-[9px] opacity-70">·{l.count}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {isLocationMissing && form.vendor && recentLocations.length === 0 && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, hospitality_location: form.vendor })}
                        className="text-[11px] text-emerald-700 hover:text-emerald-800 underline underline-offset-2 mt-1"
                        data-testid="hospitality-use-vendor"
                      >
                        → „{form.vendor}" als Ort übernehmen
                      </button>
                    )}
                  </div>
                </div>

                {/* Auto-Absetzbarkeit-Hinweis */}
                <div className="rounded-md bg-white border border-slate-200 px-3 py-2 text-xs text-slate-700">
                  <span className="font-semibold">Auto-Absetzbarkeit gesetzt:</span>{" "}
                  {hospitalityPct === 100 ? "100 %" : `${hospitalityPct} % (Bewirtungs-Regel)`}{" "}
                  — kann unten manuell angepasst werden.
                </div>
              </div>
            );
          })()}

          {/* Feb 2026 — VAT-Block: ENTWEDER Single-Tax ODER Multi-Tax-Aufschlüsselung.
              EINZIGE Wahrheit: tax_breakdown.length >= 2 → Multi-Tax. Kein User-Toggle.
              KI hat alle Werte extrahiert; User prüft nur die Summenkontrolle. */}
          {(() => {
            const isMulti = Array.isArray(form.tax_breakdown) && form.tax_breakdown.length >= 2;
            if (isMulti) return null;
            return (
              <>
                <div className="grid grid-cols-2 gap-3" data-testid="single-tax-block">
                  <div>
                    <Label>Betrag (netto)</Label>
                    <CurrencyInput
                      data-testid="expense-net-amount"
                      country={userCountry}
                      value={form.net_amount}
                      onChange={(num) => handleNetChange(num ?? 0)}
                      label={null}
                    />
                  </div>
                  <div>
                    <Label>MwSt-Satz</Label>
                    {(() => {
                      const ctry = (userCountry || "DE").toUpperCase();
                      const primary = VAT_RATES_BY_COUNTRY[ctry] || VAT_RATES_BY_COUNTRY.DE;
                      const other = VAT_RATES_OTHER_DACH[ctry] || [];
                      const allRates = [...primary, ...other];
                      const currentRate = parseFloat(form.vat_rate);
                      const ratePresentInDropdown = !isNaN(currentRate) && allRates.some((r) => r.rate === currentRate);
                      // Feb 2026 — derived: zeige Custom-Modus wenn explizit gewählt ODER Rate nicht in Presets
                      const isCustomMode = showCustomVat || !ratePresentInDropdown;
                      const dropdownValue = isCustomMode ? "__custom__" : String(currentRate);
                      return (
                        <Select
                          value={dropdownValue}
                          onValueChange={(v) => {
                            if (v === "__custom__") {
                              setShowCustomVat(true);
                              return;
                            }
                            setShowCustomVat(false);
                            handleVatChange(v);
                          }}
                        >
                          <SelectTrigger data-testid="expense-vat-rate"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {primary.map((r) => (
                              <SelectItem key={`p-${r.rate}`} value={String(r.rate)}>{r.label}</SelectItem>
                            ))}
                            {other.length > 0 && (
                              <>
                                <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400">Andere DACH-Sätze</div>
                                {other.map((r) => (
                                  <SelectItem key={`o-${r.rate}`} value={String(r.rate)}>{r.label}</SelectItem>
                                ))}
                              </>
                            )}
                            <SelectItem value="__custom__">Individuell…</SelectItem>
                          </SelectContent>
                        </Select>
                      );
                    })()}
                  </div>
                </div>
                {/* Individuelle USt-Eingabe: sichtbar wenn Custom-Mode aktiv (explizit ODER Rate nicht in Presets) */}
                {(() => {
                  const ctry = (userCountry || "DE").toUpperCase();
                  const primary = VAT_RATES_BY_COUNTRY[ctry] || VAT_RATES_BY_COUNTRY.DE;
                  const other = VAT_RATES_OTHER_DACH[ctry] || [];
                  const allRates = [...primary, ...other];
                  const currentRate = parseFloat(form.vat_rate);
                  const isCustomMode = showCustomVat || isNaN(currentRate) || !allRates.some((r) => r.rate === currentRate);
                  if (!isCustomMode) return null;
                  return (
                  <div className="-mt-1" data-testid="expense-vat-rate-custom-row">
                    <Label>Individueller MwSt-Satz (%)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="0.1"
                        min={0}
                        max={50}
                        value={Number.isFinite(parseFloat(form.vat_rate)) ? form.vat_rate : ""}
                        onChange={(e) => {
                          const str = e.target.value;
                          if (str === "") { handleVatChange("0"); return; }
                          const r = parseFloat(str);
                          if (isNaN(r)) return;
                          handleVatChange(str);
                        }}
                        className="max-w-[140px]"
                        data-testid="expense-vat-rate-custom"
                        placeholder="z.B. 5.5"
                        autoFocus
                      />
                      <span className="text-sm text-slate-500">%</span>
                    </div>
                  </div>
                  );
                })()}
                {vatAmount > 0 && (
                  <p className="text-xs text-slate-500 -mt-3" data-testid="vat-amount-display">
                    MwSt-Betrag: {vatAmount.toFixed(2)} €
                  </p>
                )}
                {expense?.vat_rate == null && (
                  <div className="-mt-3 flex flex-col gap-1.5" data-testid="vat-rate-missing-block">
                    <p className="text-xs text-amber-700" data-testid="vat-rate-missing-hint">
                      Beim Import war kein MwSt-Satz hinterlegt — bitte Satz wählen oder als <strong>0 % (steuerfrei)</strong> bestätigen.
                    </p>
                    <button
                      type="button"
                      onClick={confirmZeroVat}
                      disabled={saving}
                      className="self-start inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      data-testid="expense-confirm-zero-vat">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Als 0 % (steuerfrei) bestätigen
                    </button>
                  </div>
                )}
                {expense?.vat_rate != null && Number(form.vat_rate) === 0 && (
                  <p className="text-xs text-slate-500 -mt-3" data-testid="vat-rate-zero-hint">
                    0 % (steuerfrei) — kein Vorsteuerabzug.
                  </p>
                )}
                {(() => {
                  const foreign = getForeignRates(form, userCountry);
                  if (!foreign.length) return null;
                  const cc = (userCountry || 'DE').toUpperCase();
                  return (
                    <div className="-mt-3 rounded-md border border-amber-300 bg-amber-50 p-2" data-testid="expense-foreign-vat-hint">
                      <p className="text-xs text-amber-800">
                        <strong>Ausländischer Steuersatz</strong> ({foreign.map((r) => `${r}%`).join(', ')}): Diese USt ist nicht über deine {cc}-Voranmeldung
                        als Vorsteuer abziehbar — der Bruttobetrag zählt als Betriebsausgabe (ggf. EU-Vorsteuererstattungsverfahren).
                        Falls die Leistung dem Reverse-Charge unterliegt, kann der Lieferant die Rechnung berichtigen (netto, ohne USt).
                      </p>
                    </div>
                  );
                })()}
                {/* Edge-Case-Notfall: manueller Multi-Tax-Schalter, falls KI versagt hat */}
                <button
                  type="button"
                  onClick={() => {
                    const gross = parseFloat(form.amount) || 0;
                    const rate = parseFloat(form.vat_rate) || getCountryDefaultRate(userCountry, 0);
                    const net = rate > 0 ? Math.round((gross / (1 + rate / 100)) * 100) / 100 : gross;
                    const vat = Math.round((gross - net) * 100) / 100;
                    const secondaryRate = getCountryDefaultRate(userCountry, 1);
                    setForm((f) => ({
                      ...f,
                      multi_tax_suspected: true,
                      tax_breakdown: [
                        { rate, net, vat },
                        { rate: secondaryRate, net: 0, vat: 0 },
                      ],
                    }));
                  }}
                  className="text-[11px] text-slate-500 hover:text-slate-700 underline underline-offset-2"
                  data-testid="enable-multi-tax"
                >
                  KI hat mehrere MwSt-Sätze übersehen? Manuell aufschlüsseln
                </button>
              </>
            );
          })()}

          {/* Feb 2026 — Multi-Tax-Modus: ALLEINIGE Quelle wenn tax_breakdown.length >= 2.
              KI hat schon alle Werte gesetzt; User prüft die Summenkontrolle. */}
          {Array.isArray(form.tax_breakdown) && form.tax_breakdown.length >= 2 && (
            <div
              className="rounded-xl border-2 border-amber-300 bg-amber-50/60 p-4 space-y-3"
              data-testid="multi-tax-breakdown-editor"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">
                    Beleg mit mehreren MwSt-Sätzen
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    Z.B. Bewirtung (Speisen 7% + Getränke 19%) oder Supermarkt-Mix. Pro Satz wird eine
                    eigene Buchungszeile im DATEV-Export erzeugt (GoBD-konform).
                  </p>
                </div>
              </div>

              {/* Beleg-Brutto-Referenz (read-only, kommt aus Hauptfeld oben) */}
              <div className="flex items-center justify-between rounded-md bg-white border border-amber-200 px-3 py-2">
                <span className="text-xs text-slate-600">Beleg-Brutto (oben)</span>
                <span className="text-sm font-semibold text-slate-900" data-testid="multi-tax-reference-gross">
                  {(parseFloat(form.amount) || 0).toFixed(2)} €
                </span>
              </div>

              {/* Positionen — jeweils 1 Karte pro MwSt-Satz */}
              <div className="space-y-2.5">
                {(form.tax_breakdown || []).map((tb, idx) => {
                  const rate = parseFloat(tb.rate) || 0;
                  const net = parseFloat(tb.net) || 0;
                  const vat = parseFloat(tb.vat) || 0;
                  const gross = Math.round((net + vat) * 100) / 100;
                  const isEmpty = net === 0 && vat === 0;

                  const updateGross = (newGrossStr) => {
                    const newGross = parseFloat(newGrossStr) || 0;
                    const newBreakdown = [...form.tax_breakdown];
                    const r = newBreakdown[idx]?.rate || 0;
                    const newNet = r > 0 ? Math.round((newGross / (1 + r / 100)) * 100) / 100 : newGross;
                    const newVat = Math.round((newGross - newNet) * 100) / 100;
                    newBreakdown[idx] = { ...newBreakdown[idx], net: newNet, vat: newVat };
                    setForm((f) => ({ ...f, tax_breakdown: newBreakdown }));
                  };

                  const updateRate = (newRateStr) => {
                    const newRate = parseFloat(newRateStr) || 0;
                    const newBreakdown = [...form.tax_breakdown];
                    // Brutto behalten, Netto+MwSt aus neuem Satz neu berechnen
                    const currentGross = (parseFloat(newBreakdown[idx]?.net) || 0) + (parseFloat(newBreakdown[idx]?.vat) || 0);
                    const newNet = newRate > 0 ? Math.round((currentGross / (1 + newRate / 100)) * 100) / 100 : currentGross;
                    const newVat = Math.round((currentGross - newNet) * 100) / 100;
                    newBreakdown[idx] = { rate: newRate, net: newNet, vat: newVat };
                    setForm((f) => ({ ...f, tax_breakdown: newBreakdown }));
                  };

                  return (
                    <div
                      key={idx}
                      className={`rounded-lg border bg-white p-3 ${
                        isEmpty ? "border-rose-300 ring-1 ring-rose-200" : "border-slate-200"
                      }`}
                      data-testid={`tax-breakdown-row-${idx}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-slate-700">
                          Position {idx + 1}
                        </span>
                        {form.tax_breakdown.length > 1 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newBreakdown = form.tax_breakdown.filter((_, i) => i !== idx);
                              setForm((f) => ({
                                ...f,
                                tax_breakdown: newBreakdown,
                                multi_tax_suspected: newBreakdown.length >= 2,
                              }));
                            }}
                            className="h-6 w-6 p-0 text-rose-600 hover:bg-rose-100"
                            data-testid={`tax-breakdown-remove-${idx}`}
                            title="Position entfernen"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>

                      {/* Eingabezeile: Satz + Brutto */}
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <Label className="text-[11px] text-slate-600 mb-1 block">MwSt-Satz</Label>
                          <Select value={String(rate)} onValueChange={updateRate}>
                            <SelectTrigger
                              className="h-9 text-sm"
                              data-testid={`tax-breakdown-rate-${idx}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {(() => {
                                const c = (userCountry || "DE").toUpperCase();
                                const primary = VAT_RATES_BY_COUNTRY[c] || VAT_RATES_BY_COUNTRY.DE;
                                const other = VAT_RATES_OTHER_DACH[c] || [];
                                return (
                                  <>
                                    {primary.map((v) => (
                                      <SelectItem key={`p-${v.rate}`} value={String(v.rate)}>
                                        {v.label}
                                      </SelectItem>
                                    ))}
                                    {other.length > 0 && (
                                      <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 border-t mt-1 pt-1.5">
                                        Andere DACH-Länder
                                      </div>
                                    )}
                                    {other.map((v) => (
                                      <SelectItem key={`o-${v.rate}`} value={String(v.rate)}>
                                        {v.label}
                                      </SelectItem>
                                    ))}
                                  </>
                                );
                              })()}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-600 mb-1 block">Brutto €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={gross > 0 ? gross.toFixed(2) : ""}
                            onChange={(e) => updateGross(e.target.value)}
                            placeholder="0,00"
                            className="h-9 text-sm font-medium"
                            data-testid={`tax-breakdown-gross-${idx}`}
                          />
                        </div>
                      </div>

                      {/* Berechnete Werte — read-only Display */}
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-600 px-1">
                        <span>
                          Netto: <span className="font-semibold text-slate-900" data-testid={`tax-breakdown-net-${idx}`}>{net.toFixed(2)} €</span>
                        </span>
                        <span className="text-slate-300">·</span>
                        <span>
                          MwSt: <span className="font-semibold text-slate-900" data-testid={`tax-breakdown-vat-${idx}`}>{vat.toFixed(2)} €</span>
                        </span>
                        {isEmpty && (
                          <span className="ml-auto text-rose-700 font-medium">Bitte Brutto eingeben</span>
                        )}
                      </div>
                    </div>
                  );
                })}

                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const existing = form.tax_breakdown || [];
                    const c = (userCountry || "DE").toUpperCase();
                    // Country-aware: nimm den ersten primären Satz, der noch nicht in der Liste ist
                    const usedRates = new Set(existing.map(b => parseFloat(b.rate) || 0));
                    const primary = VAT_RATES_BY_COUNTRY[c] || VAT_RATES_BY_COUNTRY.DE;
                    let defaultRate = primary[0].rate;
                    for (const v of primary) {
                      if (!usedRates.has(v.rate)) { defaultRate = v.rate; break; }
                    }
                    const newRow = { rate: defaultRate, net: 0, vat: 0 };
                    setForm((f) => ({ ...f, tax_breakdown: [...existing, newRow] }));
                  }}
                  className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-100 w-full"
                  data-testid="tax-breakdown-add"
                >
                  + Weitere Position hinzufügen
                </Button>
              </div>

              {/* Summenprüfung — finale Validierung */}
              {(() => {
                const rows = form.tax_breakdown || [];
                const sumNet = rows.reduce((s, r) => s + (parseFloat(r.net) || 0), 0);
                const sumVat = rows.reduce((s, r) => s + (parseFloat(r.vat) || 0), 0);
                const sumGross = Math.round((sumNet + sumVat) * 100) / 100;
                const gross = parseFloat(form.amount) || 0;
                const diff = Math.round((sumGross - gross) * 100) / 100;
                const isOk = Math.abs(diff) < 0.05 && rows.length >= 1 && rows.every(r => (parseFloat(r.net) || 0) > 0 || (parseFloat(r.vat) || 0) > 0);
                return (
                  <div
                    className={`rounded-lg border-2 px-3 py-2.5 ${
                      isOk ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"
                    }`}
                    data-testid="multi-tax-sum-check"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-xs font-bold ${isOk ? "text-emerald-900" : "text-rose-900"}`}>
                        {isOk ? "✓ Summenprüfung OK" : "✗ Differenz zum Beleg-Brutto"}
                      </span>
                      {!isOk && (
                        <span className="text-xs font-medium text-rose-900" data-testid="multi-tax-diff">
                          Δ {diff > 0 ? "+" : ""}{diff.toFixed(2)} €
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <div className="text-slate-600">Σ Netto</div>
                        <div className={`font-semibold ${isOk ? "text-emerald-900" : "text-slate-900"}`}>{sumNet.toFixed(2)} €</div>
                      </div>
                      <div>
                        <div className="text-slate-600">Σ MwSt</div>
                        <div className={`font-semibold ${isOk ? "text-emerald-900" : "text-slate-900"}`}>{sumVat.toFixed(2)} €</div>
                      </div>
                      <div>
                        <div className="text-slate-600">Σ Brutto</div>
                        <div className={`font-semibold ${isOk ? "text-emerald-900" : "text-rose-900"}`}>{sumGross.toFixed(2)} €</div>
                      </div>
                    </div>
                    {!isOk && diff !== 0 && (
                      <p className="text-[11px] text-rose-800 mt-1.5">
                        Beleg-Brutto oben: <span className="font-semibold">{gross.toFixed(2)} €</span>
                        {" "}— bitte Beträge prüfen.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Zurück-zu-Single-Toggle */}
              <button
                type="button"
                onClick={() => {
                  // Behalte den Mehrheitssatz beim Wechsel zurück zu Single
                  const rows = form.tax_breakdown || [];
                  const winner = rows.length > 0
                    ? rows.reduce((a, b) => ((parseFloat(a.net) || 0) + (parseFloat(a.vat) || 0)) >= ((parseFloat(b.net) || 0) + (parseFloat(b.vat) || 0)) ? a : b)
                    : { rate: 19 };
                  const rate = parseFloat(winner.rate) || 19;
                  const gross = parseFloat(form.amount) || 0;
                  const net = rate > 0 ? Math.round((gross / (1 + rate / 100)) * 100) / 100 : gross;
                  setForm((f) => ({
                    ...f,
                    multi_tax_suspected: false,
                    tax_breakdown: [],
                    vat_rate: rate,
                    net_amount: net,
                  }));
                }}
                className="text-xs text-slate-600 hover:text-slate-800 underline underline-offset-2"
                data-testid="disable-multi-tax"
              >
                Zurück zu einem Steuersatz
              </button>
            </div>
          )}

          <Separator />

          {/* Juli 2026 — Kontextabhängige Anzeige (Entflechtung, User-Wunsch):
              Bewirtung → gesetzliche Absetzquote (VSt bleibt voll) · alle anderen
              Kategorien → nur Privatanteil (kürzt VSt + Gewinn). Verhindert die
              stille multiplikative Doppelkürzung beider Quoten. */}
          <div data-testid="deductible-section">
            {form.category === "hospitality" ? (
              <>
                <Label className="text-sm font-semibold">
                  Absetzbarer Anteil
                  <FachInfo
                    title="Bewirtungsquote"
                    testId="fach-info-bewirtung"
                    law={(userCountry || "").toUpperCase() === "AT" ? "§ 20 Abs. 1 Z 3 EStG (AT)" : "§ 4 Abs. 5 Nr. 2 EStG (DE)"}
                  >
                    Gesetzliche Abzugs-Deckelung für Bewirtungskosten: nur ein Teil (DE 70 %, AT 50 %)
                    mindert den Gewinn. Die Vorsteuer bleibt davon unberührt und ist voll abziehbar.
                  </FachInfo>
                </Label>
                <div className="flex gap-2 mt-2">
                  {[
                    { value: "full", label: "Voll (100 %)" },
                    { value: "partial", label: "Teilweise" },
                    { value: "none", label: "Nicht absetzbar" },
                  ].map((opt) => (
                    <Button
                      key={opt.value}
                      size="sm"
                      type="button"
                      data-testid={`deductible-${opt.value}`}
                      variant={form.tax_deductible_type === opt.value ? "default" : "outline"}
                      onClick={() => {
                        const partialDefault =
                          Number(form.deductible_percentage) > 0 && Number(form.deductible_percentage) < 100
                            ? form.deductible_percentage
                            : (userCountry === "AT" ? 50 : 70);
                        setForm({
                          ...form,
                          tax_deductible_type: opt.value,
                          deductible_percentage: opt.value === "full" ? 100 : opt.value === "none" ? 0 : partialDefault,
                        });
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
                {form.tax_deductible_type === "partial" && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {[70, 50].map((p) => (
                      <Button
                        key={p}
                        size="sm"
                        type="button"
                        data-testid={`deductible-chip-${p}`}
                        variant={Number(form.deductible_percentage) === p ? "default" : "outline"}
                        onClick={() => setForm({ ...form, deductible_percentage: p })}
                      >
                        {p} %
                      </Button>
                    ))}
                    <Input
                      data-testid="deductible-percentage"
                      type="number"
                      min="0"
                      max="100"
                      className="w-24"
                      value={form.deductible_percentage}
                      onChange={(e) => setForm({ ...form, deductible_percentage: e.target.value })}
                    />
                    <span className="text-sm text-slate-500">% absetzbar</span>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1.5 leading-snug">
                  Bewirtung: DE i.d.R. 70 %, AT 50 % absetzbar — die Vorsteuer bleibt voll abziehbar.
                </p>
              </>
            ) : (
              <>
                <Label className="text-sm font-semibold">
                  Privatanteil
                  <FachInfo
                    title="Privatanteil"
                    testId="fach-info-privatanteil"
                    law={(userCountry || "").toUpperCase() === "AT" ? "§ 12 Abs. 2 UStG (AT)" : "§ 15 UStG (DE)"}
                  >
                    Tatsächliche private Mitnutzung — z. B. Handy 40 % privat. Nur der betriebliche
                    Anteil wirkt steuerlich: Vorsteuer (UVA) und Betriebsausgabe werden anteilig gekürzt.
                    {(userCountry || "").toUpperCase() === "AT" && " In Österreich alternativ als Eigenverbrauch versteuerbar (volle Vorsteuer bleibt)."}
                  </FachInfo>{" "}
                  <span className="text-xs font-normal text-slate-400">(z. B. Handy/Internet teils privat)</span>
                </Label>
                {form.tax_deductible_type !== "none" && (
                  <>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {[0, 20, 40, 50].map((p) => (
                        <Button
                          key={p}
                          size="sm"
                          type="button"
                          data-testid={`private-use-chip-${p}`}
                          variant={Number(form.private_use_percentage) === p ? "default" : "outline"}
                          onClick={() => setForm({ ...form, private_use_percentage: p })}
                        >
                          {p} %
                        </Button>
                      ))}
                      <Input
                        data-testid="private-use-percentage"
                        type="number"
                        min="0"
                        max="100"
                        className="w-24"
                        value={form.private_use_percentage}
                        onChange={(e) => setForm({ ...form, private_use_percentage: e.target.value })}
                      />
                      <span className="text-sm text-slate-500">% privat</span>
                    </div>
                    {Number(form.private_use_percentage) > 0 && (userCountry || "").toUpperCase() === "AT" && (
                      <div className="mt-2 flex gap-2 flex-wrap items-center">
                        {[
                          { value: "anteilig", label: "Vorsteuer anteilig kürzen" },
                          { value: "eigenverbrauch", label: "Eigenverbrauch versteuern" },
                        ].map((m) => (
                          <Button
                            key={m.value}
                            size="sm"
                            type="button"
                            data-testid={`private-use-mode-${m.value}`}
                            variant={(form.private_use_mode || "anteilig") === m.value ? "default" : "outline"}
                            onClick={() => setForm({ ...form, private_use_mode: m.value })}
                          >
                            {m.label}
                          </Button>
                        ))}
                        <FachInfo title="Eigenverbrauch" testId="fach-info-eigenverbrauch" law="§ 3 Abs. 2 / § 3a Abs. 1a UStG (AT)">
                          Alternative zur anteiligen Kürzung: Du ziehst die volle Vorsteuer (KZ 060)
                          und versteuerst den Privatanteil stattdessen als Umsatz (KZ 000 + Satz-KZ).
                          Die Zahllast ist am Ende gleich — relevant z. B. bei überwiegend betrieblicher Nutzung.
                        </FachInfo>
                      </div>
                    )}
                    {Number(form.private_use_percentage) > 0 && (
                      <p className="text-xs text-amber-700 mt-1.5 leading-snug" data-testid="private-use-hint">
                        {(form.private_use_mode || "anteilig") === "eigenverbrauch" && (userCountry || "").toUpperCase() === "AT"
                          ? `Volle Vorsteuer bleibt abziehbar (KZ 060) — der Privatanteil (${Number(form.private_use_percentage)} %) wird als Eigenverbrauch auf der Umsatzseite versteuert (KZ 000 + Satz-KZ). Die Betriebsausgabe wird weiterhin um den Privatanteil gekürzt.`
                          : `Vorsteuer (UVA) und Betriebsausgabe werden um ${Number(form.private_use_percentage)} % gekürzt — nur der betriebliche Anteil (${100 - Number(form.private_use_percentage)} %) wirkt steuerlich.`}
                      </p>
                    )}
                  </>
                )}
                <div className="mt-3 flex items-center gap-1.5">
                  <Button
                    size="sm"
                    type="button"
                    data-testid="deductible-none"
                    variant={form.tax_deductible_type === "none" ? "default" : "outline"}
                    onClick={() =>
                      setForm({
                        ...form,
                        tax_deductible_type: form.tax_deductible_type === "none" ? "full" : "none",
                        deductible_percentage: form.tax_deductible_type === "none" ? 100 : 0,
                      })
                    }
                  >
                    Nicht absetzbar
                  </Button>
                  <FachInfo title="Nicht absetzbar" testId="fach-info-nicht-absetzbar">
                    Rein privat veranlasster Beleg: Er wird dokumentiert, zählt aber weder als
                    Betriebsausgabe (Gewinn) noch für die Vorsteuer (UVA).
                  </FachInfo>
                </div>
                {form.tax_deductible_type === "none" && (
                  <p className="text-xs text-slate-500 mt-1.5 leading-snug" data-testid="not-deductible-hint">
                    Dieser Beleg wirkt nicht auf Gewinn oder UVA — er bleibt nur zur Dokumentation erhalten.
                  </p>
                )}
                {/* Alt-Beleg-Warnung: ältere „Teilweise"-Quote (Nicht-Bewirtung) — Doppelkürzung vermeiden */}
                {form.tax_deductible_type === "partial" &&
                  Number(form.deductible_percentage) > 0 &&
                  Number(form.deductible_percentage) < 100 && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="legacy-deductible-warning">
                      <div className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                        Ältere Absetzquote aktiv: {Number(form.deductible_percentage)} %
                        <FachInfo title="Absetzquote vs. Privatanteil" testId="fach-info-legacy" side="bottom">
                          Die alte Absetzquote kürzt nur den Gewinn, der Privatanteil zusätzlich die
                          Vorsteuer. Beide zusammen wirken multiplikativ — meist ist nur der
                          Privatanteil gewollt.
                        </FachInfo>
                      </div>
                      <p className="text-[11px] text-amber-800 mt-1 leading-relaxed">
                        {Number(form.private_use_percentage) > 0
                          ? `Zusammen mit dem Privatanteil (${Number(form.private_use_percentage)} %) wirken effektiv nur ${Math.round(Number(form.deductible_percentage) * (100 - Number(form.private_use_percentage))) / 100} % als Betriebsausgabe (Doppelkürzung).`
                          : "Dieser Beleg nutzt noch die ältere Gewinn-Absetzquote. Empfohlen: auf den Privatanteil umstellen — der kürzt auch die Vorsteuer korrekt."}
                      </p>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          type="button"
                          variant="outline"
                          data-testid="legacy-convert-to-private"
                          className="h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
                          onClick={() =>
                            setForm({
                              ...form,
                              private_use_percentage: 100 - Number(form.deductible_percentage),
                              tax_deductible_type: "full",
                              deductible_percentage: 100,
                            })
                          }
                        >
                          In Privatanteil umwandeln ({100 - Number(form.deductible_percentage)} % privat)
                        </Button>
                        <Button
                          size="sm"
                          type="button"
                          variant="outline"
                          data-testid="legacy-reset-full"
                          className="h-7 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
                          onClick={() => setForm({ ...form, tax_deductible_type: "full", deductible_percentage: 100 })}
                        >
                          Quote entfernen (100 %)
                        </Button>
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>

          {/* AfA / Depreciation */}
          {expense?.treatment === "capitalized" && expense?.fixed_asset_id && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="capitalized-banner">
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                <Boxes className="h-4 w-4" />
                Dieser Beleg ist als Anlage aktiviert
              </div>
              <p className="text-xs text-emerald-700 mt-1">
                Die Felder hier sind read-only. Verwalte AfA-Plan, Buchwert und Veräußerung unter Anlagen.
              </p>
              <Link
                to={`/anlagen?asset_id=${expense.fixed_asset_id}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900 mt-2"
                data-testid="link-to-asset"
              >
                Zur Anlage <ExternalLink className="h-3 w-3" />
              </Link>
            </div>
          )}

          {afaSuggestion && expense?.treatment !== "capitalized" && (
            <div
              className={
                isPro || (afaSuggestion.type !== "linear" && afaSuggestion.type !== "pool_5y")
                  ? "bg-amber-50 border border-amber-200 rounded-lg p-3"
                  : "bg-red-50 border-2 border-red-300 rounded-lg p-3"
              }
              data-testid="afa-suggestion"
            >
              <div className={
                "flex items-center gap-2 text-sm font-semibold " +
                (isPro || (afaSuggestion.type !== "linear" && afaSuggestion.type !== "pool_5y")
                  ? "text-amber-800"
                  : "text-red-800")
              }>
                <AlertTriangle className="h-4 w-4" />
                {isPro || (afaSuggestion.type !== "linear" && afaSuggestion.type !== "pool_5y")
                  ? "Abschreibungshinweis"
                  : "Steuerlich AfA-pflichtig — Achtung!"}
              </div>
              <p className={"text-sm mt-1 " + (
                isPro || (afaSuggestion.type !== "linear" && afaSuggestion.type !== "pool_5y")
                  ? "text-amber-700"
                  : "text-red-700"
              )}>
                Nettobetrag {netAmount.toFixed(2)} € → <strong>{afaSuggestion.label}</strong>
              </p>

              {/* Free/Basic — harte Warnung mit Steuer-Folge & Upgrade-CTA */}
              {!isPro && (afaSuggestion.type === "linear" || afaSuggestion.type === "pool_5y") && (
                <>
                  <p className="text-sm text-red-700 mt-2 leading-relaxed">
                    Dieser Beleg muss steuerrechtlich über <strong>mehrere Jahre abgeschrieben</strong> werden
                    — nicht als einmalige Ausgabe im Anschaffungsmonat. Ohne korrekte AfA-Verwaltung sind
                    deine EÜR und USt-Voranmeldung <strong>nicht GoBD-konform</strong>.
                  </p>
                  <p className="text-xs text-red-600 mt-1.5">
                    Mit Pro: Anlagenspiegel · Buchwert-Tracking · § 15a-Korrekturen · Veräußerungs-Logik · DATEV-Postings je Anlage-Konto · SB-Übergabe gesperrt.
                  </p>
                  <Button
                    size="sm"
                    className="mt-2.5 bg-red-600 hover:bg-red-700 text-white shadow-sm"
                    onClick={() => (window.location.href = "/pricing")}
                    data-testid="afa-upgrade-cta"
                  >
                    <Boxes className="h-4 w-4 mr-1.5" />
                    Auf Pro upgraden — volle AfA-Verwaltung
                  </Button>
                </>
              )}

              {/* Pro-User — klassischer Hint + Aktivierung */}
              {isPro && form.depreciation_type !== afaSuggestion.type && (
                <p className="text-xs text-amber-600 mt-1">
                  Empfohlene Abschreibungsart wurde automatisch vorausgewählt. Sie können diese unten manuell ändern.
                </p>
              )}
              {/* KI-Vorschlag für Anlagen-Kategorie sichtbar machen */}
              {aiAssetCategoryHint && (
                <p className={"text-xs mt-1.5 flex items-center gap-1 " + (isPro ? "text-emerald-700" : "text-red-600")} data-testid="ai-category-hint">
                  <Sparkles className="h-3 w-3" />
                  KI-Vorschlag Kategorie: <strong>{aiAssetCategoryHint}</strong>
                </p>
              )}
              {/* Pro-Aktivierungs-CTA */}
              {isPro && (afaSuggestion.type === "linear" || afaSuggestion.type === "pool_5y") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => setActivationWizardOpen(true)}
                  data-testid="activate-as-asset-btn"
                >
                  <Boxes className="h-4 w-4 mr-1.5" />
                  Als Anlage aktivieren
                </Button>
              )}
            </div>
          )}

          {/* AfA-Methode + Plan-Tabelle wurden entfernt (Feb 2026):
              Der neue „Als Anlage aktivieren"-Workflow im AfA-Modul übernimmt
              jetzt die volle Abschreibungs-Verwaltung. Siehe afaSuggestion-Banner oben. */}

          {/* Aug 2026 — Beleg ↔ Geschäftsreise verknüpfen */}
          <TripLinkSection expense={expense} />

          {/* Notes */}
          <div>
            <Label>Notizen</Label>
            <Textarea
              data-testid="expense-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optionale Anmerkungen..."
              rows={2}
            />
          </div>

          {/* Juni 2026 — Wiederkehrende Ausgabe */}
          {expense?.is_recurring_occurrence ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center gap-2 text-sm text-slate-600" data-testid="recurring-occurrence-badge">
              <Repeat className="h-4 w-4 text-slate-500" />
              Automatisch erzeugte Folgebuchung (wiederkehrend – kein neuer Beleg)
            </div>
          ) : (
            <RecurringExpenseFields
              value={{
                is_recurring: form.is_recurring,
                recurrence_interval: form.recurrence_interval,
                recurrence_custom_value: form.recurrence_custom_value,
                recurrence_custom_unit: form.recurrence_custom_unit,
                recurrence_end_date: form.recurrence_end_date,
              }}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              idPrefix="expense-detail-recurring"
            />
          )}

          {/* SB-Anmerkungen (Advisor Comments) */}
          <AdvisorExpenseComments expenseId={expense?.id} />

          {/* Save-Status (Feb 2026 — nur transiente Hinweise, kein "Auto-Save aktiv"-Idle-Text) */}
          <div className="flex items-center justify-between gap-2 pt-2 border-t">
            <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="auto-save-status">
              {autoSaveStatus === "saving" && (
                <><Loader2 className="h-4 w-4 animate-spin text-blue-500" /> <span>Wird gespeichert…</span></>
              )}
              {autoSaveStatus === "saved" && (
                <><CheckCircle className="h-4 w-4 text-emerald-500" /> <span>Gespeichert</span></>
              )}
              {autoSaveStatus === "error" && (
                <><AlertTriangle className="h-4 w-4 text-red-500" /> <span className="text-red-600">Fehler beim Speichern</span></>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Aug 2026 — „Geprüft“-Toggle am Drawer-Ende (User-Wunsch) */}
              {expense?.id && !expense?.is_locked && (
                <button
                  type="button"
                  data-testid="expense-detail-reviewed-toggle"
                  onClick={async () => {
                    const next = !reviewed;
                    try {
                      await api.put(`/expenses/${expense.id}`, { is_reviewed: next });
                      // Sep 2026 — konsolidiert: „Geprüft" bestätigt E-Mail-Inbox-Belege mit
                      if (next && (expense?.source === 'email_inbox' || expense?.source === 'mobile_scan') && !inboxConfirmed && expense.inbox_status !== 'confirmed') {
                        try {
                          await api.post(`/inbound/expenses/${expense.id}/confirm`);
                          setInboxConfirmed(true);
                        } catch { /* Inbox-Bestätigung optional, Geprüft-Status bleibt */ }
                      }
                      setReviewed(next);
                      toast.success(next ? "Als geprüft markiert" : "Prüf-Markierung entfernt");
                      onSaved?.();
                    } catch { toast.error("Fehler beim Speichern"); }
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    reviewed
                      ? "border-green-300 bg-green-50 text-green-700 hover:bg-green-100"
                      : "border-slate-200 bg-white text-slate-600 hover:border-green-300 hover:text-green-600"}`}
                  title={reviewed ? "Prüf-Markierung entfernen" : "Als geprüft markieren"}
                >
                  <CheckCircle className={`h-4 w-4 ${reviewed ? "fill-green-100" : ""}`} />
                  {reviewed ? "Geprüft" : "Als geprüft markieren"}
                </button>
              )}
              <Button variant="outline" onClick={handleClose} data-testid="expense-close-btn">
                <X className="h-4 w-4 mr-1" /> Schließen
              </Button>
            </div>
          </div>
        </div>
          </div>
        </div>
      </SheetContent>

      {/* DSGVO AI Consent Dialog */}
      <AiConsentDialog
        isOpen={showConsentDialog}
        onClose={() => setShowConsentDialog(false)}
        onConsented={() => {
          setShowConsentDialog(false);
          if (consentAction === "reanalyze") handleReanalyzeAi();
          else handleAiAnalysis();
        }}
      />

      {/* Duplicate Warning Dialog */}
      <DuplicateWarningDialog
        isOpen={duplicateWarning.open}
        onClose={() => setDuplicateWarning({ open: false, matches: [] })}
        onConfirm={() => {
          setDuplicateWarning({ open: false, matches: [] });
          handleSave(true);
        }}
        matches={duplicateWarning.matches}
        current={duplicateWarning.current}
      />
    </Sheet>

    {/* Fullscreen receipt zoom – rendered via Portal to prevent Sheet interaction */}
    {receiptZoom && receiptPreviewUrl && receiptPreviewUrl !== "pdf" && createPortal(
      <div
        id="receipt-zoom-overlay"
        className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center cursor-pointer"
        data-testid="receipt-zoom-overlay"
      >
        <button
          type="button"
          className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/20 hover:bg-white/30 rounded-full p-3 transition-colors z-10"
          data-testid="receipt-zoom-close-btn"
        >
          <X className="h-6 w-6" />
        </button>
        <img
          src={receiptPreviewUrl}
          alt="Beleg (Vollansicht)"
          className="max-w-[95vw] max-h-[95vh] object-contain cursor-default"
        />
      </div>,
      document.body
    )}
    {/* AfA-Aktivierungs-Wizard (Feb 2026) */}
    <AssetActivationWizard
      open={activationWizardOpen}
      onClose={() => setActivationWizardOpen(false)}
      expense={expense}
      country={userCountry || "DE"}
      onActivated={() => {
        setActivationWizardOpen(false);
        onSaved?.();
        onClose?.();
      }}
    />
    </>
  );
}
function AdvisorExpenseComments({ expenseId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expenseId) return;
    setLoading(true);
    api.get(`/advisor/comments/for-expense/${expenseId}`)
      .then(r => setComments(r.data.comments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [expenseId]);

  const respond = async (commentId, action) => {
    try {
      await api.post(`/advisor/comments/${commentId}/respond`, { action });
      setComments(prev => prev.map(c => c.id === commentId ? { ...c, response_status: action } : c));
      toast.success(action === 'confirm' ? 'Bestätigt' : 'Abgelehnt');
    } catch { toast.error('Fehler'); }
  };

  if (loading || comments.length === 0) return null;

  return (
    <div className="border-t pt-3 mt-1" data-testid="expense-advisor-comments">
      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>
        Steuerberater-Anmerkungen ({comments.length})
      </div>
      <div className="space-y-2">
        {comments.map(c => (
          <div key={c.id} className="p-2 bg-slate-50 border border-slate-200 rounded text-sm" data-testid={`sb-comment-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <span className="text-[11px] text-slate-500">{c.advisor_name || 'SB'} · {new Date(c.created_at).toLocaleDateString('de-DE')}</span>
                <p className="text-slate-800 mt-0.5">{c.text}</p>
              </div>
              {c.response_status ? (
                <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${c.response_status === 'confirm' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                  {c.response_status === 'confirm' ? 'OK' : 'Nein'}
                </span>
              ) : (
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => respond(c.id, 'confirm')}
                    className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                    data-testid={`confirm-${c.id}`}>OK</button>
                  <button onClick={() => respond(c.id, 'reject')}
                    className="text-[10px] px-1.5 py-0.5 rounded border bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                    data-testid={`reject-${c.id}`}>Nein</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}